// Экран «Модель ГПР» (таблица задач) — шестой экран нового фронта
// (п.20, 2026-09-07). Встраивается в блок таблицы: шапка «Модели» (виды, столбцы,
// «Актуализировать», базовый план) остаётся за оболочкой; Гант и Сводная — тоже.
// Что здесь: группировка этап → работа → задачи, сортировка кликом по заголовку,
// правка ячеек, мультивыбор с пакетной правкой, окна «как получена дата»,
// длительности и предшественников, строка добавления ручной задачи внизу,
// виртуализация строк (в проекте до 10 тыс. задач).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { call, p, type Data } from '../api';
import { PoleSpiska } from '../Podskazki';
import { TaskCard } from '../TaskCard';
import { RabotyFiltr } from '../RabotyFiltr';
import { ShahmatkaGPR } from '../ShahmatkaGPR';
import { GantHolst, PX_MAX, PX_MIN, type HPolosa } from '../GantHolst';
import { Pop, at, type Anchor } from '../Pop';
import { confirmBox } from '../dialog';
import type { ShellCtx } from '../shell';
import { fmt, kontury, parseDate, tipColor } from '../util';

type Tasks = Data<'/api/projects/{pid}/tasks', 'get'>;
type Row = Tasks['rows'][number];
type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Floor = Data<'/api/projects/{pid}/structure', 'get'>[string][number];
type Explain = Data<'/api/tasks/{tid}/explain', 'get'>;
type LinksGet = Data<'/api/tasks/{tid}/links', 'get'>;

const D1 = 86400000;
const dnum = (s?: string | null) => (s ? Math.round(new Date(s + 'T00:00:00Z').getTime() / D1) : null);
const NO_ETAP = '— без этапа —';
const ROW_H = 25;          // высота строки таблицы, нужна для виртуализации
const MES = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MW = 54;             // ширина месяца в правой части (Гант и сводная)
const WINDOW_PAD = 12;     // строк запаса сверху и снизу окна прокрутки
// нижняя панель деталей задачи (2026-09-14): пределы высоты и её память
const DET_MIN = 160, DET_MAX = 700, DET_KEY = 'plot_detali_h';
// Гант на холсте (прототип 2026-09-18): масштаб в пикселях на день и его память
const PX_KEY = 'plot_gant_px';
function loadPx(): number {
  try {
    const n = Number(localStorage.getItem(PX_KEY));
    return n >= PX_MIN && n <= PX_MAX ? n : 3;
  } catch { return 3; }
}
function loadDetH(): number {
  try {
    const n = Number(localStorage.getItem(DET_KEY));
    return n >= DET_MIN && n <= DET_MAX ? n : 300;
  } catch { return 300; }          // приватный режим браузера
}

type Col = {
  key: string; cls?: string; grp?: string; date?: boolean;
  get: (r: Row) => string; manual: (r: Row) => boolean;
  options?: (podr: string[]) => string[];
  apply: (r: Row, v: string | null, d?: Record<string, unknown>) => void;
};
const predRuchText = (r: Row) => (r.mp || []).map(m =>
  m.nomer + (m.tip === 'НН' ? 'НН' : '') + (m.zapas ? (m.zapas > 0 ? '+' : '') + m.zapas : '') + (m.soft ? 'м' : '')).join('; ');
const num = (v: string | null) => (v ? +String(v).replace(',', '.') : null);

// колонки блока «Ручной ввод» в порядке таблицы (плюс подрядчик в блоке «Факт»)
// Колонки группы «Корректировка» (2026-09-08): исключение, объём и ручные
// предшественники переехали в карточку задачи, здесь остались правки расчёта
const HAND: Col[] = [
  { key: 'dlit_ruchnaya', get: r => String(r.dlit_r ?? ''), manual: r => r.dlit_r != null,
    apply: (r, v) => { r.dlit_r = num(v); r.dd = null; r.dlit = r.dlit_r != null ? r.dlit_r : r.dlit_avto; } },
  // декоративная длительность (#53, 2026-09-13): растягивание суммарной на Ганте
  { key: 'dlit_dekor', get: r => String(r.dd ?? ''), manual: r => r.dd != null,
    apply: (r, v) => { r.dd = num(v); r.dlit = r.dd != null ? r.dd : (r.dlit_r != null ? r.dlit_r : r.dlit_avto); } },
  { key: 'zapas', get: r => String(r.zapas ?? ''), manual: r => r.zapas != null, apply: (r, v) => { r.zapas = num(v); } },
  // рамка планера «не раньше»: ставится сдвигом суммарной полосы, снимается сама
  // при факте и ручной правке (2026-09-09)
  { key: 'ogranichenie', date: true, get: r => fmt(r.ogr), manual: r => r.ogr != null,
    apply: (r, v) => { r.ogr = v; } },
  { key: 'nachalo_vruchnuyu', date: true, get: r => fmt(r.nach_vr), manual: r => r.nach_vr != null,
    apply: (r, v) => { r.nach_vr = v; } },
  { key: 'okonchanie_vruchnuyu', date: true, get: r => fmt(r.okonch_vr), manual: r => r.okonch_vr != null,
    apply: (r, v) => { r.okonch_vr = v; } },
];
// группа «Ресурс» (2026-09-08): объём и ручной приоритет в очереди бригады
const RES: Col[] = [
  { key: 'obyom', cls: 'col-res', get: r => String(r.obyom ?? ''), manual: r => r.obyom != null,
    apply: (r, v) => { r.obyom = num(v); } },
  { key: 'prioritet_vruchnuyu', cls: 'col-res', get: r => String(r.prior ?? ''), manual: r => r.prior != null,
    apply: (r, v) => { r.prior = num(v); } },
  { key: 'nomer_brigady', cls: 'col-res', get: r => String(r.brigada ?? ''), manual: r => r.brigada != null,
    apply: (r, v) => { r.brigada = num(v); } },
  { key: 'podryadchik', cls: 'col-res', get: r => r.podryadchik ?? '', manual: () => false,
    options: podr => ['', ...podr], apply: (r, v) => { r.podryadchik = v; } },
];

// уровни сортировки: первый в списке — главный, следующие сортируют внутри него
type Sort = { i: number; dir: 1 | -1 }[];
type Display = { kind: 'etap'; name: string; min?: string; max?: string }
  | { kind: 'work'; name: string; min?: string; max?: string; open: boolean }
  | { kind: 'task'; row: Row };

export function Plan({ ctx, vid = 'table', ruch = false, summ = false, raschet = false,
  svyazi = false, oshibki = false, vidOshibki = '', onOshibki, holst = false }:
  { ctx: ShellCtx; vid?: 'table' | 'gant' | 'pivot' | 'shahm'; ruch?: boolean; summ?: boolean;
    raschet?: boolean; svyazi?: boolean;
    /** Гант на холсте (прототип 2026-09-18, переключатель «Новый Гант» у администратора) */
    holst?: boolean;
    /** кнопка «Ошибки» (владелец 2026-09-16): только задачи с замечаниями «ошибка»/«конфликт» */
    oshibki?: boolean;
    /** один вид замечания (название проверки); пусто — все */
    vidOshibki?: string;
    /** задачи с замечаниями среди отобранных панелью фильтров — число и разбивка по видам */
    onOshibki?: (s: SvodkaOshibok) => void }) {
  const [data, setData] = useState<Tasks | null>(null);
  // задачи с замечаниями (ошибка/конфликт) — подсветка строк и кнопка «Ошибки»
  const [problemy, setProblemy] = useState<Map<number, Problema>>(new Map());
  const [rows, setRows] = useState<Row[]>([]);
  const [meta, setMeta] = useState<Record<string, Work>>({});
  const [struct, setStruct] = useState<Record<string, Floor[]>>({});
  const [podr, setPodr] = useState<string[]>([]);
  const [sort, setSort] = useState<Sort>([]);
  const [openWorks, setOpenWorks] = useState<Set<string>>(new Set());
  // «Ошибки»: работы раскрываются, чтобы подсвеченные строки задач были видны сразу;
  // при выключении возвращается прежнее свёрнутое состояние
  const doOshibok = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (oshibki) {
      if (!doOshibok.current) doOshibok.current = openWorks;
      setOpenWorks(new Set((data?.rows || []).map(r => r.rabota)));
    } else if (doOshibok.current) {
      setOpenWorks(doOshibok.current);
      doOshibok.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oshibki, data]);
  const [closedEtap, setClosedEtap] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [selLast, setSelLast] = useState<number | null>(null);
  const [pop, setPop] = useState<
    | { kind: 'explain'; row: Row; at: Anchor }
    | { kind: 'dlit'; row: Row; at: Anchor }
    | { kind: 'links'; row: Row; at: Anchor }
    | { kind: 'task'; row: Row; vkladka: 'zadacha' | 'svyazi' | 'rabota' | 'shema' }
    | { kind: 'bulklinks'; at: Anchor }
    | null>(null);
  // «Расчётный»: сроки по одной модели, будто рамок нет (2026-09-09)
  const [bezRamok, setBezRamok] = useState<Record<number, { n: string | null; o: string | null }> | null>(null);
  const [scroll, setScroll] = useState(0);
  const [height, setHeight] = useState(600);
  // высота нижней панели деталей (2026-09-14): тянется мышью, запоминается
  const [detH, setDetH] = useState(loadDetH);
  // сколько пикселей занято над таблицей — нужно, чтобы панель влезла в экран
  const [sverhu, setSverhu] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const can = ctx.canPlan;
  const cols = ctx.planCols || { hand: true, res: false, pred: false, fakt: false, sprav: false, base: false, noSum: false };

  const fail = (m: string) => ctx.onError?.(m);
  // карточка задачи — всегда нижней панелью, появляется сама при клике по задаче
  // (владелец 2026-09-16: значок «Панель снизу» убран; с 2026-09-14 панель включалась им)
  const panel = pop?.kind === 'task';
  const detVnizu = panel && pop?.kind === 'task';
  const badDate = () => fail('Дата не разобрана: нужно ДД.ММ.ГГГГ');

  /** тянуть верхнюю границу панели деталей: вверх — панель выше (2026-09-14).
   *  Слушатели вешаются на документ, иначе быстрый увод мыши рвёт перетаскивание. */
  function tyanutPanel(e: React.MouseEvent) {
    e.preventDefault();
    const y0 = e.clientY, h0 = detH;
    const move = (ev: MouseEvent) =>
      setDetH(Math.min(DET_MAX, Math.max(DET_MIN, h0 + (y0 - ev.clientY))));
    const up = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      const h = Math.min(DET_MAX, Math.max(DET_MIN, h0 + (y0 - ev.clientY)));
      try { localStorage.setItem(DET_KEY, String(h)); } catch { /* приватный режим */ }
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }
  const saved = (m?: string) => { ctx.onSaved?.(m); ctx.onError?.(''); };

  /** Очистить факт задач (просьба владельца 2026-09-11): планер стирает статус, даты
   *  и процент факта прямо из модели — той же пакетной записью, что и шахматка
   *  (журнал факта ведётся). Статус из слепка СБ3 31.08 так не стирается — он живёт
   *  не в задаче, а в слепке; об этом честно предупреждаем в окне. */
  async function ochistitFakt(ids: number[]) {
    const vybrannye = rows.filter(r => ids.includes(r.id));
    const sFaktom = vybrannye.filter(estFakt);
    if (!sFaktom.length) return;
    const izSlepka = vybrannye.filter(r => r.status && !r.sv).length;
    const odna = sFaktom.length === 1 ? sFaktom[0] : null;
    const da = await confirmBox(odna
      ? 'Очистить факт задачи № ' + (odna.nomer ?? '') + ' «' + odna.rabota + '»'
        + (odna.etazh != null ? ', эт. ' + odna.etazh : '') + '?'
      : 'Очистить факт у задач: ' + sFaktom.length + '?', {
      podpis: 'Стираются статус, даты начала и окончания и процент факта; комментарий '
        + 'и подрядчик остаются. Запись попадёт в «Журнал факта»; стрелкой «назад» это можно отменить. '
        + 'Даты графика обновятся после «Актуализировать».'
        + (izSlepka ? '\n\nУ ' + izSlepka + ' из выбранных статус взят из слепка СБ3 31.08 — '
          + 'он хранится не в задаче и этой кнопкой не стирается.' : ''),
      ok: 'Очистить факт', opasno: true,
    });
    if (!da) return;
    const r = await call<{ izmeneno: number }>('post', p('/api/projects/{pid}/fakt/bulk', { pid: ctx.pid }),
      { ids: sFaktom.map(x => x.id), revs: Object.fromEntries(sFaktom.map(x => [x.id, x.rev])),
        status: null, nachalo: null, okonchanie: null, procent: null });
    if (!r.ok) { fail(r.error.message); return; }
    saved('Факт очищен у задач: ' + r.data.izmeneno + '. Нажмите «Актуализировать», чтобы обновить даты.');
    setSel(new Set()); setSelLast(null);
    void load();
  }

  const load = useCallback(async () => {
    const [t, w, s, sp] = await Promise.all([
      call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid: ctx.pid })
        + '?korpus=' + encodeURIComponent(ctx.korpus || '') + '&order=name&limit=20000'),
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Record<string, Floor[]>>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid })),
      call<{ kod: string; znachenie: string }[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
    ]);
    if (!t.ok) { fail(t.error.message); return; }
    if (w.ok) { const m: Record<string, Work> = {}; w.data.forEach(x => { m[x.name] = x; }); setMeta(m); }
    if (s.ok) setStruct(s.data);
    if (sp.ok) setPodr(sp.data.filter(x => x.kod === 'podryadchik').map(x => x.znachenie).filter(Boolean));
    setData(t.data);
    ctx.onTasks?.(t.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.korpus, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  // расчётный график считается на сервере в памяти, ничего не меняя
  useEffect(() => {
    if (!raschet) { setBezRamok(null); return; }
    let zhiv = true;
    void (async () => {
      const r = await call<{ stroki: { id: number; nachalo: string | null; okonchanie: string | null }[] }>(
        'get', p('/api/projects/{pid}/raschet', { pid: ctx.pid }));
      if (!zhiv) return;
      if (!r.ok) { fail(r.error.message); return; }
      const m: Record<number, { n: string | null; o: string | null }> = {};
      r.data.stroki.forEach(x => { m[x.id] = { n: x.nachalo, o: x.okonchanie }; });
      setBezRamok(m);
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raschet, ctx.pid, ctx.reloadToken]);

  // Esc закрывает нижнюю панель деталей — у окна это делает сам диалог,
  // а панель живёт в потоке страницы и клавиш не слышит (2026-09-14)
  useEffect(() => {
    if (!detVnizu) return;
    const esc = (e: KeyboardEvent) => {
      // поверх панели может стоять окно (карточка этажа, работы, диалог) —
      // тогда Esc принадлежит ему, панель не трогаем
      if (e.key === 'Escape' && !document.querySelector('.dlg-fon')) setPop(null);
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [detVnizu]);

  /** Задачи этой работы заведены руками, а не раскаткой (2026-09-10): признак
   *  живёт у работы — «Раскатка: вручную» или контур «Ручной». Такую задачу
   *  можно удалить поштучно; раскатанная вернётся при следующем событии. */
  const ruchnayaRabota = (name: string) => {
    const w = meta[name];
    return !!w && (w.raskatka === 'вручную' || kontury(w.kontur).includes('Ручной'));
  };

  // клиентские фильтры оболочки (работа, этаж, подрядчик); на экране «Ручное
  // планирование» — только задачи работ с раскаткой «вручную» (2026-09-08)
  useEffect(() => {
    if (!data) { setRows([]); return; }
    const poFiltram = data.rows.filter(r => (!ctx.matchesTask || ctx.matchesTask(r))
      && (!ruch || ruchnayaRabota(r.rabota)));
    const sProblemoj = poFiltram.filter(r => problemy.has(r.id));
    const vidy = new Map<string, { chto: string; gruppa: string; n: number }>();
    for (const r of sProblemoj) {
      const pr = problemy.get(r.id)!;
      for (const chto of pr.chto) {
        const v = vidy.get(chto) || { chto, gruppa: pr.gruppa, n: 0 };
        v.n += 1;
        vidy.set(chto, v);
      }
    }
    // сначала ошибки, внутри — по числу задач
    onOshibki?.({ vsego: sProblemoj.length, vidy: [...vidy.values()].sort((a, b) =>
      (a.gruppa === 'ошибка' ? 0 : 1) - (b.gruppa === 'ошибка' ? 0 : 1) || b.n - a.n) });
    const keep = !oshibki ? poFiltram
      : sProblemoj.filter(r => !vidOshibki || problemy.get(r.id)!.chto.includes(vidOshibki));
    // в режиме «Расчётный» показываем даты без рамок — сами данные не трогаем
    setRows(raschet && bezRamok
      ? keep.map(r => (bezRamok[r.id]
        ? { ...r, nachalo: bezRamok[r.id].n, okonchanie: bezRamok[r.id].o } : r))
      : keep);
    setSel(prev => { const alive = new Set(keep.map(r => r.id)); return new Set([...prev].filter(x => alive.has(x))); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ctx.filterKey, ruch, meta, raschet, bezRamok, oshibki, vidOshibki, problemy]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const set = () => {
      setHeight(el.clientHeight || 600);
      // сколько экрана занято над таблицей (шапка, фильтры, полоса кнопок):
      // по этой мерке таблица отдаёт панели деталей ровно столько, сколько
      // нужно, и панель не уезжает за нижний край окна (2026-09-14)
      const r = el.getBoundingClientRect();
      setSverhu(Math.max(0, Math.round(r.top + window.scrollY)));
    };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    window.addEventListener('resize', set);
    return () => { ro.disconnect(); window.removeEventListener('resize', set); };
    // вид меняет, какой блок измеряется: таблица или сетка шахматки (2026-09-15)
  }, [vid]);

  const planKorpus = data && (data.korpus === '*' || (data.korpus || '').includes(',')) ? '' : (data?.korpus || '');
  const etapOf = useCallback((r: Row) => meta[r.rabota]?.etap || NO_ETAP, [meta]);
  const floorOf = useCallback((r: Row) => (struct[r.korpus || planKorpus] || []).find(f => String(f.etazh) === String(r.etazh)),
    [struct, planKorpus]);

  const SORT_GET: ((r: Row) => string | number)[] = useMemo(() => [
    r => r.nomer ?? 1e12, r => r.korpus || planKorpus, r => parseFloat(String(r.etazh)) || String(r.etazh ?? ''),
    r => r.sek || r.zahvatka || '', r => r.rabota || '', r => r.nachalo || '', r => r.okonchanie || '', r => r.dlit ?? -1,
    r => (r.iskl ? 1 : 0), r => r.dlit_r ?? -1, r => r.dd ?? -1, r => r.obyom ?? -1, r => r.prior ?? -1e12, r => r.brigada ?? -1,
    r => r.zapas ?? -1, r => r.nach_vr || '', r => r.okonch_vr || '', r => predRuchText(r), r => r.pos ?? 1e12,
    r => r.ps || '', r => r.prs || '', r => r.nach_vr || '', r => r.okonch_vr || '', r => predRuchText(r),
    r => r.podryadchik || '', r => r.vn || '', r => r.vo || '', r => r.sv || '', r => r.proc ?? -1, r => r.dlit_avto ?? -1,
    r => floorOf(r)?.prioritet ?? -1e12, r => r.bln || '', r => r.bl || '',
  ], [planKorpus, floorOf]);

  // очередь «Оч.» — позиция в рамках корпуса и работы
  const rank = useMemo(() => {
    const byWk = new Map<string, Row[]>();
    rows.forEach(r => {
      const k = (r.korpus || planKorpus) + '¶' + r.rabota;
      if (!byWk.has(k)) byWk.set(k, []);
      byWk.get(k)!.push(r);
    });
    const m = new Map<number, number | ''>();
    byWk.forEach(arr => arr.slice().sort((a, b) => (a.pos ?? 1e9) - (b.pos ?? 1e9))
      .forEach((r, i) => m.set(r.id, r.pos == null ? '' : i + 1)));
    return m;
  }, [rows, planKorpus]);

  // плоский список того, что рисуем: строки этапов, работ и задач
  const display: Display[] = useMemo(() => {
    // в ручном вводе группировки нет: только внесённые вручную задачи списком.
    // Сортировка по столбцу (#54, 2026-09-13) группы больше не снимает: задачи
    // сортируются внутри своей работы, суммарные строки остаются (раньше глаз
    // «показать суммарные» при сортировке ничего не менял)
    const grouped = !cols.noSum && !ruch;
    const poSortu = (arr: Row[]) => (sort.length ? arr.slice().sort((a, b) => {
      for (const s of sort) {          // уровни по порядку: первый главный
        const g = SORT_GET[s.i];
        const x = g(a), y = g(b);
        if (x < y) return -1 * s.dir;
        if (x > y) return 1 * s.dir;
      }
      return 0;
    }) : arr);
    if (!grouped) {
      return poSortu(rows).map(row => ({ kind: 'task', row } as Display));
    }
    // этапы и работы — по самой ранней дате начала
    const wStart = new Map<string, string>(), eStart = new Map<string, string>();
    rows.forEach(r => {
      const s = r.nachalo || '9999';
      if (!wStart.has(r.rabota) || s < wStart.get(r.rabota)!) wStart.set(r.rabota, s);
      const e = etapOf(r);
      if (!eStart.has(e) || s < eStart.get(e)!) eStart.set(e, s);
    });
    const sorted = poSortu(rows).sort((a, b) => {   // устойчиво: внутри работы — порядок сортировки
      const ea = etapOf(a), eb = etapOf(b);
      if (ea !== eb) {
        if (ea === NO_ETAP) return 1;
        if (eb === NO_ETAP) return -1;
        if (eStart.get(ea) !== eStart.get(eb)) return eStart.get(ea)! < eStart.get(eb)! ? -1 : 1;
        return ea.toLowerCase() < eb.toLowerCase() ? -1 : 1;
      }
      if (a.rabota !== b.rabota) {
        if (wStart.get(a.rabota) !== wStart.get(b.rabota)) return wStart.get(a.rabota)! < wStart.get(b.rabota)! ? -1 : 1;
        return a.rabota < b.rabota ? -1 : 1;
      }
      return 0;
    });
    const out: Display[] = [];
    let curEtap: string | null = null, curWork: string | null = null;
    sorted.forEach(row => {
      const et = etapOf(row);
      if (et !== curEtap) {
        curEtap = et; curWork = null;
        const er = sorted.filter(x => etapOf(x) === et);
        out.push({ kind: 'etap', name: et,
          min: er.map(x => x.nachalo).filter(Boolean).sort()[0] as string | undefined,
          max: er.map(x => x.okonchanie).filter(Boolean).sort().slice(-1)[0] as string | undefined });
      }
      if (closedEtap.has(et)) return;
      if (row.rabota !== curWork) {
        curWork = row.rabota;
        const wr = rows.filter(x => x.rabota === curWork);
        out.push({ kind: 'work', name: row.rabota, open: openWorks.has(row.rabota),
          min: wr.map(x => x.nachalo).filter(Boolean).sort()[0] as string | undefined,
          max: wr.map(x => x.okonchanie).filter(Boolean).sort().slice(-1)[0] as string | undefined });
      }
      if (!openWorks.has(row.rabota)) return;
      out.push({ kind: 'task', row });
    });
    return out;
  }, [rows, sort, cols.noSum, closedEtap, openWorks, etapOf, SORT_GET]);

  // окно виртуализации
  // пары «предшественник → задача» для стрелок: считает ядро теми же правилами,
  // что и даты, поэтому стрелка показывает учтённую связь, а не догадку
  const [paryList, setParyList] = useState<Para[]>([]);
  useEffect(() => {
    // на холсте стрелки видны у полосы под мышью, поэтому пары нужны всегда
    if (!(svyazi || holst) || vid !== 'gant') { setParyList([]); return; }
    let zhiv = true;
    void (async () => {
      const r = await call<{ pary: Para[] }>('get',
        p('/api/projects/{pid}/svyazi-zadach', { pid: ctx.pid }));
      if (zhiv && r.ok) setParyList(r.data.pary || []);
      if (!r.ok) fail(r.error.message);
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svyazi, holst, vid, ctx.pid, ctx.reloadToken]);

  // Задачи с замечаниями (ошибки и конфликты): подсвечиваем строку прямо в
  // модели, чтобы «косяки планирования» были видны сразу, а не только на
  // экране «Проверки» (просьба владельца 2026-09-10). Перечитываем после
  // каждой правки и пересчёта — вместе с самими задачами.
  useEffect(() => {
    let zhiv = true;
    void (async () => {
      const r = await call<{ zadachi: Problema[] }>('get',
        p('/api/projects/{pid}/problemy', { pid: ctx.pid }));
      if (!zhiv) return;
      if (r.ok) setProblemy(new Map((r.data.zadachi || []).map(z => [z.tid, z])));
      else setProblemy(new Map());        // не смогли — просто не подсвечиваем
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);

  // какая работа у задачи: нужно стрелкам связей, когда группа свёрнута
  // и вместо самой задачи на экране только суммарная полоса работы
  const rabotaZadachi = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rows) m.set(r.id, r.rabota);
    return m;
  }, [rows]);

  const first = Math.max(0, Math.floor(scroll / ROW_H) - WINDOW_PAD);
  const last = Math.min(display.length, Math.ceil((scroll + height) / ROW_H) + WINDOW_PAD);
  const padTop = first * ROW_H, padBottom = Math.max(0, (display.length - last) * ROW_H);

  const grouped = !cols.noSum && !ruch;
  // --- правая часть: Гант и сводная по месяцам (2026-09-08) ---
  // слева всегда одни и те же колонки, справа меняется только визуализация
  // поэтажная шахматка (2026-09-15) рисуется вместо таблицы, в ряд со строками не встаёт
  const viz = vid === 'gant' || vid === 'pivot';
  const mesyacy = useMemo(() => {
    if (!viz) return [] as { k: string; label: string; d0: number; d1: number }[];
    // «пустая» дата листа (1899-12-30) и прочий мусор шкалу не растягивают
    const ds = (rows.flatMap(r => [r.nachalo, r.okonchanie]).filter(Boolean) as string[])
      .filter(d => d >= '2000-01-01' && d <= '2100-01-01');
    if (!ds.length) return [];
    const min = new Date(ds.reduce((a, b) => (a < b ? a : b)) + 'T00:00:00Z');
    const max = new Date(ds.reduce((a, b) => (a > b ? a : b)) + 'T00:00:00Z');
    const out: { k: string; label: string; d0: number; d1: number }[] = [];
    const cur = new Date(Date.UTC(min.getUTCFullYear(), min.getUTCMonth(), 1));
    while (cur <= max && out.length < 120) {
      const y = cur.getUTCFullYear(), m = cur.getUTCMonth();
      const d0 = Math.round(Date.UTC(y, m, 1) / D1);
      const d1 = Math.round(Date.UTC(y, m + 1, 1) / D1) - 1;
      out.push({ k: y + '-' + m, label: MES[m] + (m === 0 || !out.length ? ' ' + String(y).slice(2) : ''), d0, d1 });
      cur.setUTCMonth(m + 1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, viz]);
  // холст: шкала линейная в днях (масштаб px), с запасом в две недели справа — есть куда тянуть
  const holstOn = holst && vid === 'gant' && mesyacy.length > 0;
  const [px, setPx] = useState(loadPx);
  const smenitPx = (v: number) => {
    setPx(v);
    try { localStorage.setItem(PX_KEY, String(v)); } catch { /* приватный режим */ }
  };
  const hd0 = mesyacy.length ? mesyacy[0].d0 : 0;
  const hd1 = mesyacy.length ? mesyacy[mesyacy.length - 1].d1 + 14 : 0;
  const shirina = holstOn ? Math.ceil((hd1 - hd0 + 1) * px) : mesyacy.length * MW;
  // Пересчёт «день ↔ пиксель» идёт по фактическим месяцам (2026-09-08): колонка
  // месяца всегда MW, а дней в месяце 28–31, поэтому средний день (MW/30,44)
  // уводил полосы от сетки тем сильнее, чем дальше от начала шкалы.
  const dney_v = (m: { d0: number; d1: number }) => m.d1 - m.d0 + 1;
  /** день (серийный номер) → пиксели от левого края шкалы */
  const xDnya = (d: number) => {
    if (!mesyacy.length) return 0;
    let i = 0;
    while (i < mesyacy.length - 1 && d > mesyacy[i].d1) i++;
    const m = mesyacy[i];
    return i * MW + (d - m.d0) * (MW / dney_v(m));
  };
  /** пиксели → день (серийный номер), с округлением до дня */
  const dnyaX = (x: number) => {
    if (!mesyacy.length) return 0;
    const i = Math.max(0, Math.min(mesyacy.length - 1, Math.floor(x / MW)));
    const m = mesyacy[i];
    return m.d0 + Math.round((x - i * MW) / (MW / dney_v(m)));
  };
  /** геометрия полосы: отступ слева, ширина и дни начала/окончания */
  const polosa = (n?: string | null, o?: string | null) => {
    if (!n || !o || !mesyacy.length) return null;
    const a = dnum(n)!, b = dnum(o)!;
    const left = xDnya(a);
    return { left, width: Math.max(3, xDnya(b + 1) - left), a, b };
  };
  /** сколько задач заканчивается в этом месяце (решение владельца 2026-09-08:
      считаем по дате окончания, чтобы сумма по строке равнялась числу задач,
      а длинная работа не считалась в каждом задетом месяце) */
  const vMesyace = (list: Row[], m: { d0: number; d1: number }) =>
    list.filter(r => r.okonchanie && dnum(r.okonchanie)! >= m.d0 && dnum(r.okonchanie)! <= m.d1).length;

  // строки по работам и этапам — для подсчёта задач в месяце (сводная)
  const poRabote = useMemo(() => {
    const m = new Map<string, Row[]>();
    rows.forEach(r => { const k = r.rabota || ''; (m.get(k) || m.set(k, []).get(k)!).push(r); });
    return m;
  }, [rows]);

  // --- перетаскивание полос Ганта (2026-09-08): полосу двигаем — ставим ручное
  // начало, тянем за правый край — ручную длительность. Пока тянем, полоса
  // сдвигается на экране; отпустили — правка уходит на сервер.
  const tashim = useRef<{ id: number; x0: number; kray: boolean; row: Row;
                         a: number; b: number } | null>(null);
  // тянем суммарную полосу (режим «Корректировка суммарной», 2026-09-08):
  // храним задачи группы и её границы в днях
  const tashimSum = useRef<{ x0: number; a: number; b: number; spisok: Row[]; kluch: string;
                            kray: boolean } | null>(null);
  const [sdvigSum, setSdvigSum] = useState<{ kluch: string; dney: number; kray: boolean } | null>(null);
  const [sdvig, setSdvig] = useState<{ id: number; dney: number; kray: boolean } | null>(null);
  // полосы, ждущие пересчёта (решение владельца 2026-09-08): даты в базе меняет
  // только «Актуализировать», поэтому сразу после перетаскивания полоса остаётся
  // в новой длине и рисуется пунктиром — до ближайшего пересчёта модели
  const [zhdut, setZhdut] = useState<Record<number, { n: string; o: string }>>({});
  // то же для суммарных полос: ключ группы → ожидаемое окончание (2026-09-08)
  const [zhdutSum, setZhdutSum] = useState<Record<string, { n?: string; o?: string }>>({});
  useEffect(() => { setZhdut({}); setZhdutSum({}); }, [ctx.reloadToken, ctx.pid, ctx.korpus]);

  useEffect(() => {
    if (!can) return;
    // сдвиг в днях: считаем по той же шкале, что рисует полосу, поэтому
    // полоса во время перетаскивания стоит ровно там, где окажется после
    const dneyPoX = (t: NonNullable<typeof tashim.current>, x: number) => {
      const kray = t.kray ? t.b + 1 : t.a;
      return dnyaX(xDnya(kray) + (x - t.x0)) - kray;
    };
    // защита от случайного щелчка (владелец 2026-09-15): пока мышь ушла меньше чем
    // на POROG пикселей, это не перетаскивание — ничего не показываем и не пишем
    const POROG = 6;
    const move = (e: MouseEvent) => {
      const t = tashim.current;
      if (t && Math.abs(e.clientX - t.x0) >= POROG) setSdvig({ id: t.id, dney: dneyPoX(t, e.clientX), kray: t.kray });
      const s2 = tashimSum.current;
      if (s2 && Math.abs(e.clientX - s2.x0) >= POROG) {
        const opora = s2.kray ? s2.b + 1 : s2.a;
        const nov = dnyaX(xDnya(opora) + (e.clientX - s2.x0));
        setSdvigSum({ kluch: s2.kluch, dney: nov - opora, kray: s2.kray });
      }
    };
    const up = async (e: MouseEvent) => {
      const s2 = tashimSum.current;
      tashimSum.current = null;
      setSdvigSum(null);
      if (s2 && Math.abs(e.clientX - s2.x0) < POROG) return;   // щелчок, а не перетаскивание
      if (s2 && mesyacy.length) {
        const opora = s2.kray ? s2.b + 1 : s2.a;
        await tyanulGruppu(s2.spisok, s2.kluch, s2.a, s2.b, s2.kray,
          dnyaX(xDnya(opora) + (e.clientX - s2.x0)));
        return;
      }
      const t = tashim.current;
      tashim.current = null;
      setSdvig(null);
      if (!t || !mesyacy.length) return;
      if (Math.abs(e.clientX - t.x0) < POROG) return;             // щелчок, а не перетаскивание
      const dney = dneyPoX(t, e.clientX);
      if (!dney) return;
      await tyanulZadachu(t.row, t.kray, dney);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, mesyacy, rows]);

  /** Записать перетаскивание полосы задачи (общее для старого Ганта и холста):
   *  правый край — ручная длительность, вся полоса — ручное начало. Без подтверждения
   *  (владелец 2026-09-15: «после каждой правки не надо спрашивать»): от случайной даты
   *  защищает порог POROG, ошибку снимает стрелка «назад». */
  async function tyanulZadachu(r: Row, kray: boolean, dney: number) {
    if (kray) {
      const dlit = Math.max(1, Math.round((r.dlit || 1) + dney));
      const ok = await sohranit(r, { dlit_ruchnaya: dlit },
        'Длительность «' + r.rabota + '» эт. ' + r.etazh + ': ' + dlit + ' дн.');
      if (ok && r.nachalo) zhdatPolosu(r.id, r.nachalo, plusDney(r.nachalo, dlit));
    } else {
      const nach = r.nach_vr || r.nachalo;
      if (!nach) return;
      const iso = plusDney(nach, dney);
      const ok = await sohranit(r, { nachalo_vruchnuyu: iso },
        'Ручное начало «' + r.rabota + '» эт. ' + r.etazh + ': ' + fmt(iso));
      if (ok) zhdatPolosu(r.id, iso, plusDney(iso, Math.max(1, r.dlit || 1)));
    }
  }

  /** Записать перетаскивание суммарной полосы (общее для старого Ганта и холста).
   *  `a`, `b` — дни полосы до перетаскивания; `novoe` — новый день: у правого края —
   *  день после окончания, у всей полосы — начало. */
  async function tyanulGruppu(spisok: Row[], kluch: string, a: number, b: number, kray: boolean,
    novoe: number) {
    if (kray) {                     // правый край: меняем длительности
      const konec = novoe;
      const bylo = b + 1 - a, stalo = konec - a;
      if (bylo > 0 && stalo > 0 && stalo !== bylo) {
        const ok = await rastyanut(spisok, stalo / bylo);
        // даты пересчитывает «Актуализировать», поэтому до пересчёта показываем
        // полосу в новой длине пунктиром — как у обычных задач
        // начало, если группу уже сдвигали, сохраняем: иначе после растягивания
        // полоса возвращалась на исходное место (замечание владельца 2026-09-09)
        if (ok) setZhdutSum(z2 => ({ ...z2, [kluch]: { n: z2[kluch]?.n, o: dnyaISO(konec - 1) } }));
      }
      return;
    }
    // вся полоса (владелец 2026-09-15, вариант А): новое начало группы одной
    // датой «не раньше» на её задачи; дальше их раскладывает расчёт
    const nach = novoe;
    if (nach !== a) {
      const ok = await sdvinutGruppu(spisok, dnyaISO(nach));
      if (ok) setZhdutSum(z2 => ({ ...z2, [kluch]: { n: dnyaISO(nach), o: dnyaISO(b + nach - a) } }));
    }
  }

  /** дата ISO плюс сколько-то дней */
  const plusDney = (iso: string, n: number) => {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const zhdatPolosu = (id: number, n: string, o: string) => setZhdut(z => ({ ...z, [id]: { n, o } }));

  /** день (серийный номер) → дата ISO */
  const dnyaISO = (d: number) => new Date(d * D1).toISOString().slice(0, 10);

  /** растянуть группу: длительности незавершённых задач меняются пропорционально */
  async function rastyanut(spisok: Row[], k: number) {
    const ids = spisok.map(r => r.id);
    const r = await call<{ izmeneno: number; propuscheno: number }>('post',
      p('/api/projects/{pid}/tasks/masshtab', { pid: ctx.pid }), { ids, k });
    if (!r.ok) { fail(r.error.message); return false; }
    saved('Длительность изменена у задач: ' + r.data.izmeneno
      + (r.data.propuscheno ? '; законченных не тронуто: ' + r.data.propuscheno : '')
      + '. Нажмите «Актуализировать», чтобы обновить даты.');
    void load();
    return true;
  }

  /** сдвинуть группу (владелец 2026-09-15, вариант А): всем задачам без факта и ручных
      дат одна дата «не раньше» — новое начало группы; дальше их раскладывает расчёт */
  async function sdvinutGruppu(spisok: Row[], nachalo: string) {
    const ids = spisok.map(x => x.id);
    const r = await call<{ izmeneno: number; propuscheno: number }>('post',
      p('/api/projects/{pid}/tasks/sdvig', { pid: ctx.pid }), { ids, nachalo });
    if (!r.ok) { fail(r.error.message); return false; }
    if (!r.data.izmeneno) {
      fail('Двигать нечего: у всех задач группы факт или ручные даты');
      return false;
    }
    saved('Группа начнётся не раньше ' + fmt(nachalo) + ': дату получили задачи: ' + r.data.izmeneno
      + (r.data.propuscheno ? '; с фактом и ручными датами не тронуто: ' + r.data.propuscheno : '')
      + '. Нажмите «Актуализировать», чтобы пересчитать даты.');
    void load();
    return true;
  }

  /** сбросить корректировки группы (двойной клик по суммарной полосе) */
  async function sbrositSdvig(spisok: Row[]) {
    // владелец 2026-09-15: сброс снимает всё, что сделала корректировка суммарной
    // (декоративные «не раньше» и длительность), а ручные — отдельным согласием
    const dekor = spisok.filter(x => x.ogr || x.dd != null).length;
    const ruch = spisok.filter(x => x.dlit_r != null || x.nach_vr || x.okonch_vr).length;
    if (!dekor && !ruch) { saved('У этой группы корректировок нет — даты уже по модели.'); return; }
    if (dekor && !await confirmBox('Сбросить корректировки суммарной у этой группы?', {
      podpis: 'Декоративные сроки («не раньше» и растянутая длительность) уберутся у задач: '
        + dekor + '. Отменить можно стрелкой «назад».', ok: 'Сбросить' })) return;
    const ruchnye = ruch > 0 && await confirmBox('Сбросить и ручные корректировки?', {
      podpis: 'У задач группы: ' + ruch + ' — ручная длительность, начало или окончание. Их ставят '
        + 'в таблице и карточке задачи, раньше их ставило и растягивание полосы (до 13.09). '
        + 'Отменить можно стрелкой «назад».', ok: 'Сбросить и ручные', opasno: true });
    if (!dekor && !ruchnye) return;
    const ids = spisok.map(x => x.id);
    const r = await call<{ izmeneno: number }>('post',
      p('/api/projects/{pid}/tasks/sdvig', { pid: ctx.pid }), { ids, sbros: true, ruchnye });
    if (!r.ok) { fail(r.error.message); return; }
    saved('Корректировки сброшены у задач: ' + r.data.izmeneno
      + '. Нажмите «Актуализировать», чтобы пересчитать даты.');
    void load();
  }

  /** записать правку задачи с полосы Ганта */
  async function sohranit(row: Row, telo: Record<string, unknown>, soobshchenie: string) {
    const r = await call<{ rev: number }>('patch', p('/api/tasks/{tid}', { tid: row.id }),
      { rev: row.rev, ...telo });
    if (!r.ok) { fail(r.error.message); return false; }
    saved(soobshchenie.replace(/\.$/, '') + '. Полоса показана пунктиром: нажмите «Актуализировать», чтобы пересчитать даты.');
    void load();
    return true;
  }

  /** ячейки правой части: полоса Ганта или колонки-месяцы сводной */
  const vizYacheyki = (n?: string | null, o?: string | null, spisok?: Row[], vidPolosy?: string,
    row?: Row) => {
    if (holstOn) {
      // полосу рисует холст (GantHolst) поверх: здесь только место нужной ширины
      return (
        <td className="viz-c" style={{ padding: 0 }} onClick={e => e.stopPropagation()}>
          <div style={{ width: shirina, height: 1 }} />
        </td>
      );
    }
    if (vid === 'gant') {
      const zh = row ? zhdut[row.id] : undefined;
      const kluchSum0 = spisok ? (n || '') + '¶' + (o || '') + '¶' + spisok.length : '';
      const zhSum = spisok ? zhdutSum[kluchSum0] : undefined;
      const g = polosa(zh ? zh.n : (zhSum?.n || n), zh ? zh.o : (zhSum?.o || o));
      const s2 = row && sdvig && sdvig.id === row.id ? sdvig : null;
      // ключ суммарной строки — по её границам и числу задач
      const kluchSum = spisok ? (n || '') + '¶' + (o || '') + '¶' + spisok.length : '';
      const ss = spisok && sdvigSum && sdvigSum.kluch === kluchSum ? sdvigSum : null;
      let left = g ? g.left : 0;
      let width = g ? g.width : 0;
      if (g && s2) {                       // тянем задачу: считаем по целым дням
        const a2 = s2.kray ? g.a : g.a + s2.dney;
        const b2 = g.b + s2.dney;
        left = xDnya(a2);
        width = Math.max(3, xDnya(b2 + 1) - left);
      }
      if (g && ss) {                       // тянем суммарную: за край — длина, за полосу — вся группа
        if (ss.kray) {
          width = Math.max(3, xDnya(g.b + 1 + ss.dney) - g.left);
        } else {
          left = xDnya(g.a + ss.dney);
          width = Math.max(3, xDnya(g.b + 1 + ss.dney) - left);
        }
      }
      // полоса задачи ставит ручные сроки (владелец 2026-09-15): тянется в Ганте, щелчок
      // (сдвиг меньше POROG) ничего не пишет, подтверждения нет — отмена стрелкой
      const tyanem = !!row && can && summ && vid === 'gant' && !raschet;
      const tyanemSum = !!spisok && can && summ && vid === 'gant' && !!g && !raschet;
      return (
        <td className="viz-c" style={{ padding: 0 }} onClick={e => e.stopPropagation()}>
          <div className="viz-pole" style={{ width: shirina }}>
            {mesyacy.map((m, i) => <i key={m.k} className="viz-set" style={{ left: i * MW }} />)}
            {g && <div className={'viz-bar ' + (vidPolosy || '') + (raschet ? ' raschet' : '')
              + (tyanem ? ' tyanem' : '')
              + (tyanemSum ? ' tyanem sum-drag' : '') + (zh || zhSum ? ' zhdet' : '')}
              style={{ left, width }}
              title={raschet ? fmt(n) + ' — ' + fmt(o) + '  ·  расчётный график: сроки по одной модели, без рамок'
                : zh ? fmt(zh.n) + ' — ' + fmt(zh.o) + '  ·  ждёт пересчёта: нажмите «Актуализировать»'
                : tyanemSum ? fmt(n) + ' — ' + fmt(o)
                  + '  ·  тяните полосу — группа начнётся не раньше новой даты (одна дата на все '
                  + 'задачи); за правый край — длительности незавершённых задач; двойной клик — '
                  + 'сбросить корректировки группы'
                : tyanem ? fmt(n) + ' — ' + fmt(o) + '  ·  тяните полосу — ручное начало, за правый край — '
                  + 'ручная длительность; отменить — стрелкой «назад»'
                : fmt(n) + ' — ' + fmt(o)}
              onClick={e => { e.stopPropagation(); }}
              onDoubleClick={e => {
                if (!tyanemSum || !spisok) return;
                e.preventDefault(); e.stopPropagation();
                void sbrositSdvig(spisok);
              }}
              onMouseDown={e => {
                if (tyanemSum && spisok) {
                  // суммарная (владелец 2026-09-15): правый край — длительности,
                  // середина — одна дата «не раньше» на всю группу (вариант А)
                  const box3 = (e.target as HTMLElement).getBoundingClientRect();
                  e.preventDefault(); e.stopPropagation();
                  tashimSum.current = { x0: e.clientX, a: g.a, b: g.b, spisok,
                                        kray: box3.right - e.clientX <= 9,
                                        kluch: (n || '') + '¶' + (o || '') + '¶' + spisok.length };
                  return;
                }
                if (!tyanem || !row) return;
                e.preventDefault(); e.stopPropagation();
                const box2 = (e.target as HTMLElement).getBoundingClientRect();
                tashim.current = { id: row.id, x0: e.clientX, kray: box2.right - e.clientX <= 7,
                                  row, a: g.a, b: g.b };
              }} />}
          </div>
        </td>
      );
    }
    return (
      <>
        {mesyacy.map(m => {
          const k = spisok ? vMesyace(spisok, m)
            : (o && dnum(o)! >= m.d0 && dnum(o)! <= m.d1 ? 1 : 0);
          return <td key={m.k} className={'num viz-m' + (k ? ' est' : '')}
            onClick={e => e.stopPropagation()}>{k || ''}</td>;
        })}
      </>
    );
  };

  // hOn обязан равняться числу колонок HAND (#52, 2026-09-13: было 4 при пяти колонках —
  // ячейки строк этапов и работ съезжали, и раскраска блоков сбивалась)
  const hOn = cols.hand ? HAND.length : 0, rOn = cols.res ? 4 : 0, fOn = cols.fakt ? 4 : 0;
  // столбец «Ошибка» — кратко, что не так (владелец 2026-09-16); вне списка HEAD, потому что
  // номер столбца HEAD — это уровень сортировки (SORT_GET); в «Ручном вводе» не показываем:
  // строка добавления разложена по столбцам таблицы
  const stolbecOsh = oshibki && !ruch;
  const sOn = (cols.sprav ? 2 : 0) + (cols.base ? 2 : 0);
  void sOn;
  // ячейки-заглушки строк этапов и работ — в цветах своих блоков, чтобы полосы блоков
  // шли через всю таблицу (замечание владельца #52, 2026-09-13)
  const zaglushki = (
    <>
      {hOn > 0 && <td className="col-hand" colSpan={hOn}></td>}
      {rOn > 0 && <td className="col-res" colSpan={rOn}></td>}
      <td className="col-pred" colSpan={6}></td>
      {fOn > 0 && <td className="col-fakt" colSpan={fOn}></td>}
      {cols.sprav && <td className="col-sprav" colSpan={2}></td>}
      {cols.base && <td className="col-base" colSpan={2}></td>}
    </>
  );
  const spanTail = (grouped: boolean) => 7 + (grouped ? 0 : 0);
  void spanTail;

  async function patchTask(row: Row, key: string, val: string | null, col: Col) {
    const r = await call<Record<string, unknown>>('patch', p('/api/tasks/{tid}', { tid: row.id }), { rev: row.rev, [key]: val });
    if (!r.ok) {
      fail(r.error.message);
      if (r.error.code === 'stale') void load();
      return;
    }
    col.apply(row, val, r.data);
    if (typeof r.data.rev === 'number') row.rev = r.data.rev;
    saved();
    setRows(rs => rs.slice());
  }

  function clickRow(e: React.MouseEvent, row: Row) {
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey) return;
    e.preventDefault(); e.stopPropagation();
    const next = new Set(sel);
    if (e.shiftKey && selLast != null) {
      const vis = display.filter(d => d.kind === 'task').map(d => (d as { row: Row }).row.id);
      const a = vis.indexOf(selLast), b = vis.indexOf(row.id);
      if (a >= 0 && b >= 0) vis.slice(Math.min(a, b), Math.max(a, b) + 1).forEach(x => next.add(x));
    } else if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
    setSel(next); setSelLast(row.id);
  }
  function clickWorkGroup(e: React.MouseEvent, name: string) {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      e.preventDefault(); e.stopPropagation();
      const ids = rows.filter(x => x.rabota === name).map(x => x.id);
      const all = ids.length && ids.every(id => sel.has(id));
      const next = new Set(sel);
      ids.forEach(id => { if (all) next.delete(id); else next.add(id); });
      setSel(next);
      return;
    }
    setOpenWorks(s => { const n = new Set(s); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  }

  const headClick = (i: number) => {
    if (!SORT_GET[i]) return;
    setSort(s => {
      const j = s.findIndex(x => x.i === i);
      if (j < 0) return [...s, { i, dir: 1 as const }];        // новый уровень в конец
      if (s[j].dir === 1) return s.map((x, k) => (k === j ? { i, dir: -1 as const } : x));
      return s.filter((_, k) => k !== j);                      // третий клик — снять
    });
  };
  /** стрелка направления и номер уровня, если сортировка не одна */
  const arrow = (i: number) => {
    const j = sort.findIndex(x => x.i === i);
    if (j < 0) return '';
    return (sort[j].dir === 1 ? ' ▲' : ' ▼') + (sort.length > 1 ? String(j + 1) : '');
  };

  // ---- холст (прототип 2026-09-18): полосы по строкам display и место задачи для стрелок ----
  const poEtapu = useMemo(() => {
    const m = new Map<string, Row[]>();
    rows.forEach(r => { const k = etapOf(r); (m.get(k) || m.set(k, []).get(k)!).push(r); });
    return m;
  }, [rows, etapOf]);
  /** задачи суммарной строки: этап или работа */
  const spisokGruppy = (d: Display) => (d.kind === 'etap' ? poEtapu.get(d.name) || []
    : d.kind === 'work' ? poRabote.get(d.name) || [] : []);
  const holstPolosy = useMemo(() => {
    if (!holstOn) return [] as (HPolosa | null)[];
    return display.map((d): HPolosa | null => {
      if (d.kind === 'task') {
        const r = d.row, zh = zhdut[r.id];
        const n = zh ? zh.n : r.nachalo, o = zh ? zh.o : r.okonchanie;
        if (!n || !o) return null;
        return { a: dnum(n)!, b: dnum(o)!, vid: 'zad', zhdet: !!zh, proc: r.proc ?? null,
          sost: r.sv === 'СМР окончены' || r.sv === 'СМР приняты' ? 'done' : r.sv === 'СМР начаты' ? 'act' : '',
          podpis: '№ ' + (r.nomer ?? '—') + ' · ' + r.rabota + ' · ' + (r.korpus || planKorpus)
            + (r.etazh != null ? ', эт. ' + r.etazh : '') + '\n' + fmt(n) + ' — ' + fmt(o)
            + (r.dlit != null ? ' · ' + r.dlit + ' дн.' : '') + (r.sv ? ' · ' + r.sv : '')
            + (r.proc != null ? ' ' + r.proc + '%' : '') + (zh ? '\nЖдёт пересчёта: нажмите «Пересчитать»' : '') };
      }
      const spisok = spisokGruppy(d);
      const zh = zhdutSum[(d.min || '') + '¶' + (d.max || '') + '¶' + spisok.length];
      const n = zh?.n || d.min, o = zh?.o || d.max;
      if (!n || !o) return null;
      return { a: dnum(n)!, b: dnum(o)!, vid: d.kind === 'etap' ? 'etap' : 'rab', zhdet: !!zh, proc: null, sost: '',
        podpis: (d.kind === 'etap' ? 'Этап «' : 'Работа «') + d.name + '» · задач: ' + spisok.length
          + '\n' + fmt(n) + ' — ' + fmt(o) + (zh ? '\nЖдёт пересчёта: нажмите «Пересчитать»' : '') };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holstOn, display, zhdut, zhdutSum, poEtapu, poRabote, planKorpus]);
  /** где на экране задача: своя строка, а если свёрнута — строка работы или этапа */
  const gdeZadacha = useMemo(() => {
    const m = new Map<number, number>();
    if (!holstOn) return m;
    const rab = new Map<string, number>(), etp = new Map<string, number>();
    display.forEach((d, i) => {
      if (d.kind === 'task') m.set(d.row.id, i);
      else if (d.kind === 'work') rab.set(d.name, i);
      else etp.set(d.name, i);
    });
    for (const r of rows) {
      if (m.has(r.id)) continue;
      const i = rab.get(r.rabota) ?? etp.get(etapOf(r));
      if (i != null) m.set(r.id, i);
    }
    return m;
  }, [holstOn, display, rows, etapOf]);
  const holstTyanul = (di: number, kray: boolean, dney: number, a: number, b: number) => {
    const d = display[di];
    if (!d) return;
    if (d.kind === 'task') { void tyanulZadachu(d.row, kray, dney); return; }
    const spisok = spisokGruppy(d);
    void tyanulGruppu(spisok, (d.min || '') + '¶' + (d.max || '') + '¶' + spisok.length, a, b, kray,
      kray ? b + 1 + dney : a + dney);
  };
  const holstDvojnoj = (di: number) => {
    const d = display[di];
    if (!d) return;
    if (d.kind === 'task') setPop({ kind: 'task', row: d.row, vkladka: 'zadacha' });
    else if (can && !raschet) void sbrositSdvig(spisokGruppy(d));
  };

  // столбец «Секц.» (он же захватка) обычно скрыт; показываем, как только у задач
  // появились секции (#70, 2026-09-18) — иначе планер не видит, где А, а где Б
  const estSek = rows.some(r => r.sek);
  const tableCls = 'hide-prior hide-brig' + (estSek ? '' : ' hide-zahv')
    + (cols.hand ? '' : ' hide-hand') + (cols.pred ? '' : ' hide-pred')
    + (cols.sprav ? '' : ' hide-sprav') + (cols.fakt ? '' : ' hide-fakt') + (cols.base ? '' : ' hide-base')
    + (cols.res ? '' : ' hide-res');

  return (
    <div>
      {sel.size > 0 && <SelBar count={sel.size} podr={podr} can={can}
        ruchnyh={[...sel].filter(id => {
          const r = rows.find(x => x.id === id);
          return !!r && ruchnayaRabota(r.rabota);
        }).length}
        sFaktom={rows.filter(r => sel.has(r.id) && estFakt(r)).length}
        onClearFakt={() => ochistitFakt([...sel])}
        onClear={() => { setSel(new Set()); setSelLast(null); }}
        onLinks={e => setPop({ kind: 'bulklinks', at: at(e) })}
        onDelete={async () => {
          const r = await call<{ udaleno: number; propuscheno: number }>('post',
            p('/api/projects/{pid}/tasks/udalit', { pid: ctx.pid }), { ids: [...sel] });
          if (!r.ok) { fail(r.error.message); return; }
          saved('Удалено задач: ' + r.data.udaleno
            + (r.data.propuscheno ? ' (пропущено раскатанных: ' + r.data.propuscheno + ')' : '')
            + '. Нажмите «Актуализировать», чтобы обновить даты.');
          setSel(new Set()); setSelLast(null);
          void load();
        }}
        onApply={async body => {
          const r = await call<{ izmeneno: number }>('post', p('/api/projects/{pid}/tasks/bulk', { pid: ctx.pid }),
            { ids: [...sel], ...body });
          if (!r.ok) { fail(r.error.message); return false; }
          saved('Записано задач: ' + r.data.izmeneno + '. Нажмите «Актуализировать», чтобы обновить даты.');
          setSel(new Set()); setSelLast(null);
          void load();
          return true;
        }} />}

      {/* position: relative обязателен (#50/#57, 2026-09-13): слой стрелок позиционируется
          от этого контейнера; без него он привязывался к странице, и при любом изменении
          высоты шапки или панели фильтров стрелки уезжали от полос */}
      {/* поэтажная шахматка (владелец 2026-09-15): «этаж × работа», дата окончания в клетке */}
      {vid === 'shahm' ? (
        <div>
          <div className="shg-filtr">
            <RabotyFiltr raboty={Object.keys(meta).sort((a, b) => a.localeCompare(b, 'ru'))} />
          </div>
          {/* клик по клетке — сведения о работе в нижней панели (владелец 2026-09-15);
              сетка уступает панели высоту так же, как таблица */}
          <ShahmatkaGPR rows={rows} struct={struct} meta={meta} boxRef={box}
            tekId={pop?.kind === 'task' ? pop.row.id : null}
            maxHeight={panel ? 'calc(100vh - ' + (sverhu + detH + 30) + 'px)' : undefined}
            onOpen={row => setPop({ kind: 'task', row, vkladka: 'rabota' })} />
        </div>
      ) : (
      <div className="tbl" ref={box}
        style={{ position: 'relative',
          // панель деталей забирает высоту у таблицы: окно прокрутки меряется
          // от контейнера, поэтому виртуализация подстроится сама
          ...(panel
            ? { maxHeight: 'calc(100vh - ' + (sverhu + detH + 30) + 'px)', minHeight: 120 }
            : null) }}
        onScroll={e => setScroll((e.target as HTMLDivElement).scrollTop)}>
        {/* стрелки связей рисуются поверх таблицы, координаты берутся у самих
            полос — так стрелка не разъезжается с полосой (2026-09-10) */}
        {holstOn && <GantHolst box={box} polosy={holstPolosy} d0={hd0} d1={hd1} px={px} onPx={smenitPx}
          pary={paryList} gde={gdeZadacha} tyanem={can && summ && !raschet} raschet={raschet}
          onTyanul={holstTyanul} onDvojnoj={holstDvojnoj}
          vybrana={pop?.kind === 'task' ? gdeZadacha.get(pop.row.id) : undefined} />}
        {svyazi && vid === 'gant' && !holstOn && <SvyaziSloj box={box} pary={paryList} rabotaZadachi={rabotaZadachi}
          risuj={scroll + display.length + first + openWorks.size + closedEtap.size
            + tableCls.length * 1000 + (raschet ? 7 : 0)} />}   {/* #50: блоки столбцов сдвигают полосы */}
        <table id="planTable" className={tableCls}>
          <thead>
            <tr className="gh">
              {/* фильтр по работе — над «Работой», рядом с «Расчётом» (владелец 2026-09-15) */}
              <th colSpan={estSek ? 5 : 4} className="fx-h">
                <RabotyFiltr raboty={Object.keys(meta).sort((a, b) => a.localeCompare(b, 'ru'))} />
              </th>
              <th colSpan={3} className="col-itog col-b">Расчёт</th>
              {stolbecOsh && <th className="col-osh">Ошибка</th>}
              {/* блоки столбцов остаются при Ганте и сводной, визуализация встаёт
                  правее них (владелец, #47 2026-09-13; раньше Гант вытеснял блоки) */}
              {cols.hand && <th colSpan={HAND.length} className="col-hand">Сроки</th>}
              {cols.res && <th colSpan={4} className="col-res">Ресурс</th>}
              {cols.pred && <th colSpan={6} className="col-pred">Предшественники</th>}
              {cols.fakt && <th colSpan={4} className="col-fakt">Факт</th>}
              {cols.sprav && <th colSpan={2} className="col-sprav">Справочник</th>}
              {cols.base && <th colSpan={2} className="col-base">Базовый</th>}
              {viz && <th className="viz-h" colSpan={vid === 'gant' ? 1 : mesyacy.length}>
                {vid === 'gant' ? 'Гант' : 'Сводная: задач заканчивается в месяце'}</th>}
            </tr>
            <tr>
              {HEAD.map((h, i) => (
                <th key={h.t + i} className={h.c} title={h.ttl} onClick={() => headClick(i)}>{h.t + arrow(i)}</th>
              )).flatMap((th, i) => (stolbecOsh && i === HEAD_ITOG_POSLEDNIJ
                ? [th, <th key="osh" className="col-osh w-osh"
                    title="Кратко, что не так с задачей: ● — ошибка, ○ — конфликт. Разбор и что делать — на экране «Проверки»">
                    Что не так</th>]
                : [th]))}
              {viz && vid === 'gant' && holstOn && (
                <th className="viz-c" style={{ padding: 0 }}><div style={{ width: shirina }} /></th>
              )}
              {viz && vid === 'gant' && !holstOn && (
                <th className="viz-c" style={{ padding: 0 }}>
                  <div className="viz-shkala" style={{ width: shirina }}>
                    {mesyacy.map(m => <span key={m.k} style={{ width: MW }}>{m.label}</span>)}
                  </div>
                </th>
              )}
              {viz && vid === 'pivot' && mesyacy.map(m => (
                <th key={m.k} className="num viz-m">{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {padTop > 0 && <tr style={{ height: padTop }}><td colSpan={33} style={{ padding: 0, border: 0 }} /></tr>}
            {display.slice(first, last).map((d, i) => {
              if (d.kind === 'etap') {
                const closed = closedEtap.has(d.name);
                return (
                  <tr key={'e' + d.name + i} data-di={first + i} className="grp etp" style={{ height: ROW_H }}
                    onClick={() => setClosedEtap(s => { const n = new Set(s); if (n.has(d.name)) n.delete(d.name); else n.add(d.name); return n; })}>
                    {/* плюс/минус в рамке, как в MS Project (владелец 2026-09-15) */}
                    <td className="num fx-n"><span className="pm">{closed ? '+' : '−'}</span></td><td className="fx-k"></td>
                    <td className="fx-e"></td><td className="col-zahv"></td>
                    <td className="fx-w" title={d.name}>{d.name}</td>
                    <td className="col-itog col-b">{d.min ? fmt(d.min) : ''}</td>
                    <td className="col-itog col-ok">{d.max ? fmt(d.max) : ''}</td>
                    <td className="num col-itog col-e" title="длительность этапа: окончание − начало">
                      {d.min && d.max ? dnum(d.max)! - dnum(d.min)! : ''}</td>
                    {stolbecOsh && <td className="col-osh"></td>}
                    {zaglushki}
                    {viz && vizYacheyki(d.min, d.max, rows.filter(r => (meta[r.rabota]?.etap || NO_ETAP) === d.name), 'sum-etap')}
                  </tr>);
              }
              if (d.kind === 'work') {
                return (
                  <tr key={'w' + d.name + i} data-grp={d.name} data-di={first + i} className="grp"
                    style={{ height: ROW_H }} onClick={e => clickWorkGroup(e, d.name)}>
                    <td className="num fx-n"><span className="pm">{d.open ? '−' : '+'}</span></td><td className="fx-k"></td>
                    <td className="fx-e"></td><td className="col-zahv"></td>
                    <td className="fx-w" title="Клик по строке — развернуть или свернуть работу"
                      style={{ paddingLeft: 24 }}>{d.name}</td>
                    <td className="col-itog col-b">{d.min ? fmt(d.min) : ''}</td>
                    <td className="col-itog col-ok">{d.max ? fmt(d.max) : ''}</td>
                    <td className="num col-itog col-e" title="длительность: окончание − начало">
                      {d.min && d.max ? dnum(d.max)! - dnum(d.min)! : ''}</td>
                    {stolbecOsh && <td className="col-osh"></td>}
                    {zaglushki}
                    {viz && vizYacheyki(d.min, d.max, poRabote.get(d.name), 'sum-rab')}
                  </tr>);
              }
              const row = d.row;
              const rk = row.korpus || planKorpus;
              const fi = floorOf(row);
              const cs = fi?.tip && fi.tip !== 'Типовой' ? tipColor(fi.tip) : null;
              const blDelta = row.bl && row.okonchanie ? dnum(row.okonchanie)! - dnum(row.bl)! : 0;
              return (
                <tr key={row.id} data-tid={row.id} data-di={first + i}
                  className={(row.ruchnoe ? 'ruch' : '') + (row.iskl ? ' iskl' : '')
                    + (sel.has(row.id) ? ' psel' : '')
                    // чья карточка открыта в нижней панели (2026-09-14)
                    + (detVnizu && pop?.kind === 'task' && pop.row.id === row.id ? ' det-tek' : '')
                    + (problemy.get(row.id)
                      ? ' prob' + (problemy.get(row.id)!.gruppa === 'ошибка' ? ' prob-osh' : '')
                      : '')}
                  title={problemy.get(row.id)
                    ? (problemy.get(row.id)!.gruppa === 'ошибка' ? 'Ошибка: ' : 'Конфликт: ')
                      + problemy.get(row.id)!.chto.join('; ')
                      + '. Разбор — на экране «Проверки»'
                    : undefined}
                  style={{ height: ROW_H }} onClickCapture={e => clickRow(e, row)}>
                  {/* № — системный ид задачи, не редактируется (владелец 2026-09-08);
                      им ссылаются на задачу в ручных предшественниках */}
                  <td className="num fx-n pw-link"
                    title={'№ ' + (row.nomer ?? '—') + ' — системный номер задачи (по нему её указывают '
                      + 'в ручных предшественниках). Клик — карточка задачи'}
                    onClick={e => { e.stopPropagation(); setPop({ kind: 'task', row, vkladka: 'zadacha' }); }}>
                    {row.nomer ?? ''}</td>
                  <td className="fx-k" title={rk || undefined}>{rk || ''}</td>
                  <td className="num fx-e pw-link" title="Открыть карточку этажа"
                    style={cs ? { background: cs.bg, borderLeft: '3px solid ' + cs.bd } : undefined}
                    onClick={e => { e.stopPropagation(); if (rk && row.etazh != null) ctx.onFloorCard?.(rk, String(row.etazh)); }}>
                    {row.etazh ?? ''}</td>
                  {/* секция внутри этажа (#70); у задач без секции — захватка, как было */}
                  <td className="col-zahv" title={row.sek ? 'Секция ' + row.sek : undefined}>
                    {row.sek || row.zahvatka || ''}</td>
                  {/* карточка одна: клик по работе открывает её на вкладке «Работа» (2026-09-08) */}
                  <td className="fx-w pw-link" title="Открыть карточку: вкладка «Работа»"
                    style={grouped ? { paddingLeft: 44 } : undefined}
                    onClick={e => { e.stopPropagation(); setPop({ kind: 'task', row, vkladka: 'rabota' }); }}>
                    {row.rabota || ''}
                    {/* захватки размечены на схеме (#109): факт по этой задаче отмечается
                        на чертеже, а в шахматке показывается уже посчитанный итог */}
                    {row.shema && (
                      <span className="zn-shema"
                        title="Захватки размечены на схеме: факт отмечается на чертеже. Клик — вкладка «Схема»"
                        onClick={e => { e.stopPropagation(); setPop({ kind: 'task', row, vkladka: 'shema' }); }}>▦</span>
                    )}</td>
                  <td className="col-itog col-b expl" title="клик — как получена дата"
                    onClick={e => setPop({ kind: 'explain', row, at: at(e) })}>{fmt(row.nachalo)}</td>
                  <td className="col-itog col-ok expl" title="клик — как получена дата"
                    onClick={e => setPop({ kind: 'explain', row, at: at(e) })}>{fmt(row.okonchanie)}</td>
                  <td className="num col-itog col-e expl dlx" title="итоговая длительность: ручная или автоматическая. Клик — задать ручную"
                    onClick={e => setPop({ kind: 'dlit', row, at: at(e) })}>{row.dlit ?? ''}</td>
                  {stolbecOsh && (() => {
                    const pr = problemy.get(row.id);
                    const tekst = pr ? pr.chto.join('; ') : '';
                    return <td className={'col-osh' + (pr?.gruppa === 'ошибка' ? ' osh' : '')} title={tekst || undefined}>
                      {pr ? (pr.gruppa === 'ошибка' ? '● ' : '○ ') + tekst : ''}</td>;
                  })()}
                  {cols.hand && HAND.map(col => (
                    <EditTd key={col.key} row={row} col={col} can={can} podr={podr} onBadDate={badDate}
                      cls={'num col-hand' + (col.cls ? ' ' + col.cls : '') + (col.manual(row) ? ' manual' : '')}
                      onSave={(v, c) => patchTask(row, col.key, v, c)} />
                  ))}
                  {cols.res && RES.map(col => (
                    <EditTd key={col.key} row={row} col={col} can={can} podr={podr} onBadDate={badDate}
                      cls={'num col-res' + (col.manual(row) ? ' manual' : '')}
                      onSave={(v, c) => patchTask(row, col.key, v, c)} />
                  ))}
                  {cols.pred && <>
                    <td className="num col-pred" title="очередь в рамках корпуса и работы">{rank.get(row.id) ?? ''}</td>
                    <td className={'col-pred expl lnkc' + (row.ml || row.lo ? ' lnk-on' : '')}
                      title={'срок предшественника по связи. Клик — предшественники задачи'
                        + (row.ml ? '; ручных связей: ' + row.ml : '') + (row.lo ? '; отключено штатных: ' + row.lo : '')}
                      onClick={() => setPop({ kind: 'task', row, vkladka: 'svyazi' })}>{fmt(row.ps)}</td>
                    <td className="col-pred" title="окончание предыдущего этажа бригады">{fmt(row.prs)}</td>
                    <td className={'col-pred' + (row.nach_vr ? ' manual' : '')} title="ручное начало задачи">{fmt(row.nach_vr)}</td>
                    <td className={'col-pred' + (row.okonch_vr ? ' manual' : '')} title="ручное окончание задачи">{fmt(row.okonch_vr)}</td>
                    <td className={'col-pred lnkc' + (row.mp && row.mp.length ? ' manual' : '')}
                      title="ручные предшественники (номера задач). Клик — назначить в карточке задачи"
                      onClick={() => setPop({ kind: 'task', row, vkladka: 'svyazi' })}>
                      {predRuchText(row)}</td>
                  </>}
                  {cols.fakt && (() => {
                    // клик по факту — очистить его (планер, 2026-09-11); с Ctrl/Shift — выделение
                    const chist = can && estFakt(row);
                    const fk = (e: React.MouseEvent) => {
                      if (chist && !e.ctrlKey && !e.metaKey && !e.shiftKey) void ochistitFakt([row.id]);
                    };
                    const cl = chist ? ' fakt-chist' : '';
                    const tt = chist ? 'Клик — очистить факт задачи' : undefined;
                    return <>
                      <td className={'num col-fakt' + cl} title={tt} onClick={fk}>{row.vn ? fmt(row.vn) : ''}</td>
                      <td className={'num col-fakt' + cl} title={tt} onClick={fk}>{row.vo ? fmt(row.vo) : ''}</td>
                      <td className={'col-fakt' + cl} title={tt} onClick={fk}>{row.sv || ''}</td>
                      <td className={'num col-fakt' + cl} title={tt} onClick={fk}>{row.proc != null ? row.proc + '%' : ''}</td>
                    </>;
                  })()}
                  {cols.sprav && <>
                    <td className="num col-sprav">{row.dlit_avto ?? ''}</td>
                    <td className="num col-sprav">{fi?.prioritet ?? ''}</td>
                  </>}
                  {cols.base && <>
                    <td className="col-base">{fmt(row.bln)}</td>
                    <td className={'col-base' + (blDelta > 0 ? ' late' : blDelta < 0 ? ' early' : '')}
                      title={blDelta > 0 ? 'позже базового на ' + blDelta + ' дн' : blDelta < 0 ? 'раньше базового на ' + -blDelta + ' дн' : ''}>
                      {fmt(row.bl)}</td>
                  </>}
                  {viz && vizYacheyki(row.nachalo, row.okonchanie, undefined,
                    row.sv === 'СМР окончены' || row.sv === 'СМР приняты' ? 'done'
                      : row.sv === 'СМР начаты' ? 'act' : '', row)}
                </tr>);
            })}
            {padBottom > 0 && <tr style={{ height: padBottom }}><td colSpan={33} style={{ padding: 0, border: 0 }} /></tr>}
            {/* строка быстрого ввода — только в «Ручном планировании»
                (владелец 2026-09-08): из модели её убрали */}
            {can && ruch && <AddTaskRow ctx={ctx} meta={meta} struct={struct}
              korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')}
              onAdded={(m) => { saved(m); void load(); }} onFail={fail} />}
          </tbody>
        </table>
      </div>
      )}

      {/* нижняя панель деталей (просьба владельца 2026-09-14): та же карточка
          задачи, но под таблицей — строку видно, соседнюю задачу открывают
          кликом, панель переключается на неё. Появляется сама при клике по задаче,
          закрывается крестиком или Esc (владелец 2026-09-16) */}
      {panel && (
        <div className="det-panel" style={{ height: detH }}>
          <div className="det-grip" title="Тяните вверх или вниз — панель станет выше или ниже"
            onMouseDown={tyanutPanel} />
          <div className="det-body">
            {/* key: при переходе на другую задачу карточка пересобирается,
                иначе на мгновение осталась бы прежняя */}
            <TaskCard key={pop.row.id} pid={ctx.pid} row={pop.row} korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')}
              works={Object.keys(meta).sort()} struct={struct} podr={podr} can={can}
              ruchnaya={ruchnayaRabota(pop.row.rabota)} vnizu
              vkladka={pop.vkladka} onClose={() => setPop(null)}
              onChanged={m => { saved(m); void load(); }} onFail={fail} />
          </div>
        </div>
      )}

      <div className="pw-muted" style={{ marginTop: 8 }}>
        {data ? (data.korpus === '*' ? 'Все корпуса' : 'Корпус ' + data.korpus) + ': показано ' + rows.length
          + ' из ' + data.total + ' задач. ' : ''}
        {oshibki ? 'Показаны только задачи с замечаниями: розовая строка — ошибка, песочная — конфликт; '
          + 'что именно не так — в подсказке строки (наведите мышь). Все задачи — повторным нажатием «Ошибки». '
          : ''}
        {ruch ? 'Показаны задачи работ с раскаткой «вручную». Строка ＋ внизу: работа, '
          + 'длительность и предшественник; Enter — сохранить, можно вставить список из буфера. '
          : ''}
        Жёлтая строка — ручное планирование/факт; синее — введено вручную; клик — правка.
        Ctrl+клик — выделить строки для пакетной правки (Shift — диапазон, Ctrl+клик по группе — вся работа).
      </div>

      {pop?.kind === 'explain' && <ExplainPop at={pop.at} row={pop.row} onClose={() => setPop(null)} onFail={fail}
        onLinks={e => setPop({ kind: 'links', row: pop.row, at: at(e) })} />}
      {pop?.kind === 'dlit' && <DlitPop at={pop.at} row={pop.row} work={meta[pop.row.rabota]} korpus={planKorpus}
        onClose={() => setPop(null)} onFail={fail}
        onSaved={() => { setPop(null); saved(); void load(); }} />}
      {pop?.kind === 'links' && <LinksPop at={pop.at} row={pop.row} korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')} works={Object.keys(meta).sort()}
        struct={struct} planKorpus={planKorpus} can={can} onClose={() => setPop(null)} onFail={fail}
        onSaved={() => { saved(); void load(); }} />}
      {pop?.kind === 'bulklinks' && <BulkLinksPop at={pop.at} pid={ctx.pid} ids={[...sel]} korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')}
        works={Object.keys(meta).sort()} struct={struct} onClose={() => setPop(null)} onFail={fail}
        onDone={m => { setPop(null); saved(m); setSel(new Set()); void load(); }} />}
    </div>
  );
}

/** Индекс «Длит.» блока «Расчёт» в HEAD: после него встаёт столбец «Ошибка» */
const HEAD_ITOG_POSLEDNIJ = 7;
/** Заголовки второй строки шапки — порядок должен совпадать с SORT_GET */
const HEAD: { t: string; c: string; ttl?: string }[] = [
  { t: '№', c: 'col-nom', ttl: '№ задачи (ИД): по нему задаются ручные предшественники. Клик — изменить номер' },
  { t: 'Корп.', c: 'col-korp', ttl: 'Корпус' }, { t: 'Эт.', c: 'col-et', ttl: 'Этаж' }, { t: 'Секц.', c: 'col-zahv', ttl: 'Секция внутри этажа (у работ с раскаткой «по этажам и секциям»); у остальных задач здесь захватка' }, { t: 'Работа', c: 'col-work' },
  { t: 'Начало', c: 'col-itog col-b w-d', ttl: 'Итоговое расчётное начало. Клик по дате в строке — как она получена' },
  { t: 'Окончание', c: 'col-itog col-ok w-d', ttl: 'Итоговое расчётное окончание. Клик по дате в строке — как она получена' },
  { t: 'Длит.', c: 'col-itog col-e w-n', ttl: 'Итоговая длительность: ручная, а если пусто — из справочника' },
  { t: 'Длит.', c: 'col-hand w-n', ttl: 'Ручная длительность задачи — решение планера (пусто — действует длительность из справочника работы)' },
  { t: 'Декор. длит.', c: 'col-hand w-n', ttl: 'Декоративная длительность: ставится растягиванием суммарной полосы на Ганте. Расчёт учитывает её, «Расчётный» показывает график без неё; снимается сама при факте или ручной правке' },
  { t: 'Сдвиг начала', c: 'col-hand w-n', ttl: 'Запас: на сколько дней задача начинается позже, чем позволяет расчёт' },
  { t: 'Декор. начало', c: 'col-hand w-d', ttl: 'Декоративное начало «не раньше»: сдвиг суммарной полосы ставит его всей группе одной датой. Расчёт учитывает, «Расчётный» показывает график без него; снимается двойным кликом по суммарной полосе, при факте или ручной правке' },
  { t: 'Начало', c: 'col-hand w-d', ttl: 'Ручное начало задачи: фиксирует строку' },
  { t: 'Оконч.', c: 'col-hand w-d', ttl: 'Ручное окончание задачи: фиксирует строку' },
  { t: 'Объём', c: 'col-res w-n', ttl: 'Объём задачи: длительность = объём ÷ (выработка × численность работы)' },
  { t: 'Приоритет', c: 'col-res w-n', ttl: 'Ручной приоритет в очереди бригады: меньше — раньше' },
  { t: 'Бригада', c: 'col-res w-n', ttl: 'Номер бригады: разные бригады одной работы идут параллельно' },
  { t: 'Подрядчик', c: 'col-res', ttl: 'Подрядчик задачи — из списка «Подрядчики» в «Списках»' },
  { t: 'Оч.', c: 'col-pred w-n', ttl: 'Расчётная позиция задачи в очереди в рамках корпуса и работы' },
  { t: 'По связи', c: 'col-pred w-d', ttl: 'Срок предшественника по связи (окончание, для НН — начало)' },
  { t: 'По ресурсу', c: 'col-pred w-d', ttl: 'Окончание предыдущего этажа той же бригады' },
  { t: 'Нач. вруч.', c: 'col-pred w-d' }, { t: 'Оконч. вруч.', c: 'col-pred w-d' }, { t: 'Ручн. предш.', c: 'col-pred' },
  { t: 'Нач. факт', c: 'col-fakt w-d' }, { t: 'Оконч. факт', c: 'col-fakt w-d' }, { t: 'Статус факт', c: 'col-fakt' },
  { t: '% факт', c: 'col-fakt w-n' }, { t: 'Авто длит.', c: 'col-sprav w-n' }, { t: 'Авто приор.', c: 'col-sprav w-n' },
  { t: 'Баз. начало', c: 'col-base w-d' },
  { t: 'Баз. оконч.', c: 'col-base w-d', ttl: 'красным — расчёт позже базы, зелёным — раньше' },
];

/** Ячейка с правкой по клику: текст → поле ввода или список */
function EditTd({ row, col, can, cls, title, podr, onSave, onBadDate }: {
  row: Row; col: Col; can: boolean; cls?: string; title?: string; podr: string[];
  onSave: (v: string | null, col: Col) => Promise<void>; onBadDate: () => void;
}) {
  const [edit, setEdit] = useState(false);
  const value = col.get(row);
  if (!edit) {
    return <td className={(cls || '') + (can ? ' edit' : '')} title={title}
      onClick={e => { if (can && !e.ctrlKey && !e.metaKey && !e.shiftKey) setEdit(true); }}>{value}</td>;
  }
  const finish = async (save: boolean, raw: string) => {
    setEdit(false);
    if (!save) return;
    const t = raw.trim();
    if (t === value) return;
    let val: string | null = t === '' ? null : t;
    if (col.date) {
      const iso = t === '' ? null : parseDate(t);
      // неразобранная дата: сообщение пользователю, на сервер ничего не уходит
      if (iso === undefined) { onBadDate(); return; }
      val = iso;
    }
    await onSave(val, col);
  };
  const opts = col.options ? col.options(podr) : null;
  return (
    <td className={(cls || '') + ' editing'} title={title}>
      {opts
        ? <select autoFocus defaultValue={value} onChange={e => void finish(true, e.target.value)} onBlur={() => setEdit(false)}>
            {(opts.includes(value) ? opts : [value, ...opts]).map((o, i) => <option key={o + i} value={o}>{o}</option>)}
          </select>
        : <input autoFocus defaultValue={value} style={{ width: '100%' }}
            onBlur={e => void finish(true, e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void finish(true, (e.target as HTMLInputElement).value);
              if (e.key === 'Escape') setEdit(false);
            }} />}
    </td>
  );
}

/** есть ли у задачи ручной факт (статус, даты или процент) */
const estFakt = (r: { sv?: string | null; vn?: string | null; vo?: string | null; proc?: number | null }) =>
  !!(r.sv || r.vn || r.vo || r.proc != null);

function SelBar({ count, ruchnyh, sFaktom, podr, can, onApply, onClear, onLinks, onDelete, onClearFakt }: {
  count: number;
  /** сколько из выбранных с фактом — у них его можно очистить (2026-09-11) */
  sFaktom: number; onClearFakt: () => void;
  /** сколько из выбранных заведено вручную — только их можно удалить (2026-09-10) */
  ruchnyh: number;
  podr: string[]; can: boolean;
  onApply: (body: Record<string, unknown>) => Promise<boolean>; onClear: () => void;
  onLinks: (e: React.MouseEvent) => void; onDelete: () => Promise<void>;
}) {
  const empty = { dlit: '', obyom: '', prior: '', brig: '', zapas: '', nach: '', okonch: '', podr: '', iskl: '' };
  const [v, setV] = useState(empty);
  const f = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setV(s => ({ ...s, [k]: e.target.value }));
  async function apply() {
    const body: Record<string, unknown> = {};
    let any = false;
    const numF = (raw: string, key: string) => { const t = raw.trim(); if (t === '') return; any = true; body[key] = t === '-' ? null : t; };
    numF(v.dlit, 'dlit_ruchnaya'); numF(v.obyom, 'obyom'); numF(v.prior, 'prioritet_vruchnuyu');
    numF(v.brig, 'nomer_brigady'); numF(v.zapas, 'zapas');
    for (const [raw, key] of [[v.nach, 'nachalo_vruchnuyu'], [v.okonch, 'okonchanie_vruchnuyu']] as [string, string][]) {
      const t = raw.trim();
      if (t === '') continue;
      any = true;
      if (t === '-') { body[key] = null; continue; }
      const iso = parseDate(t);
      if (iso === undefined) return;
      body[key] = iso;
    }
    if (v.podr) { any = true; body.podryadchik = v.podr === '-' ? null : v.podr; }
    if (v.iskl) { any = true; body.isklyuchena = v.iskl === 'да'; }
    if (!any) return;
    if (await onApply(body)) setV(empty);
  }

  /** удаление выбранного: спрашиваем один раз и честно говорим, что уйдёт */
  async function udalit() {
    const da = await confirmBox('Удалить выбранные задачи: ' + ruchnyh + '?', {
      podpis: (count > ruchnyh
        ? 'Из ' + count + ' выбранных удалятся только заведённые вручную — ' + ruchnyh
          + '; созданные раскаткой останутся (их убирают корпусом в карточке работы'
          + ' или галочкой «Исключить из модели»).\n\n'
        : '')
        + 'Вместе с задачами уйдут их ручные связи, отметки факта и базовые даты.'
        + ' Это необратимо: отмене (стрелкой «назад») удаление не подлежит.',
      ok: 'Удалить', opasno: true,
    });
    if (da) await onDelete();
  }
  return (
    <div className="bar" style={{ background: '#eef3fd', border: '1px solid #bcd0f7', borderRadius: 8, padding: '6px 10px', margin: '6px 0' }}>
      <b>Выбрано: {count}</b>
      <label>Длит. <input style={{ width: 60 }} value={v.dlit} onChange={f('dlit')} /></label>
      <label>Объём <input style={{ width: 70 }} value={v.obyom} onChange={f('obyom')} /></label>
      <label>Приор. <input style={{ width: 60 }} value={v.prior} onChange={f('prior')} /></label>
      <label>Бриг. <input style={{ width: 60 }} value={v.brig} onChange={f('brig')} /></label>
      <label>Подрядчик <select value={v.podr} onChange={f('podr')}>
        <option value="">— не менять —</option><option value="-">— очистить —</option>
        {podr.map(x => <option key={x} value={x}>{x}</option>)}</select></label>
      <label>Зап. <input style={{ width: 60 }} value={v.zapas} onChange={f('zapas')} /></label>
      <label>Нач. вруч. <input style={{ width: 100 }} placeholder="ДД.ММ.ГГГГ" value={v.nach} onChange={f('nach')} /></label>
      <label>Оконч. вруч. <input style={{ width: 100 }} placeholder="ДД.ММ.ГГГГ" value={v.okonch} onChange={f('okonch')} /></label>
      <label title="Исключить выбранные задачи из модели или вернуть">Искл. <select value={v.iskl} onChange={f('iskl')}>
        <option value="">— не менять —</option><option value="да">исключить</option><option value="-">вернуть</option></select></label>
      {can && <button onClick={() => void apply()}>Применить к выбранным</button>}
      {can && <button className="sec" title="Назначить выбранным задачам ручного предшественника" onClick={onLinks}>Предшественник…</button>}
      {can && ruchnyh > 0 && (
        <button className="dlg-opasno"
          title="Удалить выбранные задачи ручных работ; задачи, созданные раскаткой, останутся"
          onClick={() => void udalit()}>Удалить задачи ({ruchnyh})</button>
      )}
      {can && sFaktom > 0 && (
        <button className="dlg-opasno" title="Стереть статус, даты и процент факта у выбранных задач"
          onClick={onClearFakt}>Очистить факт ({sFaktom})</button>
      )}
      <button className="sec" onClick={onClear}>Снять выделение</button>
      <span className="pw-muted">пусто — не менять, «-» — очистить</span>
    </div>
  );
}

function ExplainPop({ at: a, row, onClose, onFail, onLinks }:
  { at: Anchor; row: Row; onClose: () => void; onFail: (m: string) => void; onLinks: (e: React.MouseEvent) => void }) {
  const [d, setD] = useState<Explain | null>(null);
  useEffect(() => {
    void (async () => {
      const r = await call<Explain>('get', p('/api/tasks/{tid}/explain', { tid: row.id }));
      if (!r.ok) { onFail(r.error.message); onClose(); return; }
      setD(r.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);
  if (!d) return null;
  const tipName: Record<string, string> = { 'ручное': 'задано вручную', 'факт': 'зафиксировано по факту',
    'расчёт': 'расчёт по модели', 'исключена': 'исключена из модели' };
  return (
    <Pop at={a} title={d.rabota + ' · этаж ' + d.etazh + ' — ' + (tipName[d.tip] || d.tip)} onClose={onClose} width={460}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>
        Начало {d.nachalo ? fmt(d.nachalo) : '—'} · Окончание {d.okonchanie ? fmt(d.okonchanie) : '—'}
        {' · длительность ' + (d.dlit || 0) + ' дн · запас ' + (d.zapas || 0) + (d.status ? ' · факт: ' + d.status : '')}
      </div>
      <div style={{ fontSize: 13 }}>
        {d.tip === 'расчёт' && d.varianty.length > 0 && <>
          <div className="pw-muted">Начало — самый поздний из вариантов:</div>
          {d.varianty.map((v, i) => (
            <div key={i} style={{ padding: '3px 0', fontWeight: v.vybran ? 600 : undefined }}>
              {(v.vybran ? '✓ ' : '· ') + v.opisanie + ' → ' + fmt(v.data)}</div>))}
        </>}
        {(d.istochniki || []).map((t, i) => <div key={i} style={{ padding: '3px 0' }}>{'· ' + t}</div>)}
      </div>
      <div className="bar" style={{ margin: '10px 0 0' }}>
        <button className="sec" title="Штатные и ручные связи задачи" onClick={onLinks}>Предшественники…</button>
      </div>
    </Pop>
  );
}

function DlitPop({ at: a, row, work, korpus, onClose, onSaved, onFail }: {
  at: Anchor; row: Row; work?: Work; korpus: string; onClose: () => void; onSaved: () => void; onFail: (m: string) => void }) {
  const [v, setV] = useState(row.dlit_r != null ? String(row.dlit_r) : '');
  const src = row.obyom != null && work?.trud && work?.chel
    ? 'объём ' + row.obyom + ' ÷ (выработка ' + work.trud + ' × ' + work.chel + ' чел.)' : 'из справочника работ';
  async function save(val: string | null) {
    const r = await call('patch', p('/api/tasks/{tid}', { tid: row.id }), { rev: row.rev, dlit_ruchnaya: val });
    if (!r.ok) { onFail(r.error.message); return; }
    onSaved();
  }
  return (
    <Pop at={a} title={row.rabota + ' · ' + (row.korpus || korpus) + (row.etazh ? ' · эт. ' + row.etazh : '')}
      onClose={onClose} width={380}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', margin: '10px 0', alignItems: 'center' }}>
        <span>Автоматическая</span>
        <span><b>{row.dlit_avto != null ? row.dlit_avto : '—'}</b> дн{' '}
          <span className="pw-muted">{row.dlit_avto != null ? '(' + src + ')' : '(длительность работе не задана)'}</span></span>
        <span>Ручная</span>
        <input autoFocus style={{ width: 90 }} placeholder="пусто = авто" value={v} onChange={e => setV(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void save(v.trim() === '' ? null : v.trim()); }} />
      </div>
      <div className="pw-muted" style={{ margin: '0 0 10px' }}>Ручная длительность важнее автоматической.
        Даты пересчитаются после «Актуализировать».</div>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => {
          const t = v.trim();
          if (t !== '' && isNaN(+t.replace(',', '.'))) { onFail('Длительность — число дней'); return; }
          void save(t === '' ? null : t);
        }}>Сохранить</button>
        <button className="sec" onClick={() => void save(null)}>Очистить ручную</button>
      </div>
    </Pop>
  );
}

function LinksPop({ at: a, row, korpusa, works, struct, planKorpus, can, onClose, onSaved, onFail }: {
  at: Anchor; row: Row; korpusa: string[]; works: string[]; struct: Record<string, Floor[]>; planKorpus: string;
  can: boolean; onClose: () => void; onSaved: () => void; onFail: (m: string) => void }) {
  const [d, setD] = useState<LinksGet | null>(null);
  const [f, setF] = useState({ korpus: row.korpus || planKorpus || '', rabota: '', etazh: row.etazh ? String(row.etazh) : '',
    tip: 'ОН', zapas: '', soft: false });
  const reload = useCallback(async () => {
    const r = await call<LinksGet>('get', p('/api/tasks/{tid}/links', { tid: row.id }));
    if (!r.ok) { onFail(r.error.message); onClose(); return; }
    setD(r.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);
  useEffect(() => { void reload(); }, [reload]);
  if (!d) return null;
  const dd = (x?: string | null) => (x ? fmt(x) : '—');
  async function toggleOtkl(pred: string, otkl: boolean) {
    const r = await call('post', p('/api/tasks/{tid}/links/otkl', { tid: row.id }), { pred, otkl });
    if (!r.ok) { onFail(r.error.message); return; }
    row.lo = (row.lo || 0) + (otkl ? 1 : -1);
    onSaved(); void reload();
  }
  async function delRuch(id: number) {
    const r = await call('delete', p('/api/task-links/{lid}', { lid: id }));
    if (!r.ok) { onFail(r.error.message); return; }
    row.ml = Math.max(0, (row.ml || 0) - 1);
    onSaved(); void reload();
  }
  async function add() {
    if (!f.rabota.trim() || !f.korpus) { onFail('Укажите работу и корпус предшественника'); return; }
    const r = await call('post', p('/api/tasks/{tid}/links', { tid: row.id }), {
      korpus: f.korpus, rabota: f.rabota.trim(), etazh: f.etazh || null, tip: f.tip, zapas: f.zapas.trim() || null, soft: f.soft });
    if (!r.ok) { onFail(r.error.message); return; }
    row.ml = (row.ml || 0) + 1;
    setF(s => ({ ...s, rabota: '', zapas: '' }));
    onSaved(); void reload();
  }
  return (
    <Pop at={a} title={d.zadacha.rabota + ' · ' + d.zadacha.korpus + (d.zadacha.etazh ? ' · эт. ' + d.zadacha.etazh : ' · корпус целиком')}
      onClose={onClose} width={520}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Начало задачи — самый поздний из предшественников.
        «Из справочника» — предшественники работы (галочка снята = на этой задаче связь не действует);
        «только у этой задачи» — точечные связи, могут вести на другой этаж или корпус.</div>
      <div style={{ fontWeight: 600, margin: '4px 0' }}>Из справочника (общие для всех этажей)</div>
      {!d.shtatnye.length && <div className="pw-muted">нет</div>}
      {d.shtatnye.map((s, i) => (
        <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={!s.otkl} disabled={!can}
            title="действует на этой задаче" onChange={e => void toggleOtkl(s.pred, !e.target.checked)} />
          <span style={s.otkl ? { color: '#98a2b3', textDecoration: 'line-through' } : undefined}>
            {s.pred + ' (' + s.tip + (s.zapas ? ', задержка ' + s.zapas : '') + (s.oper ? ', ↑' + s.oper + ' эт' : '')
              + (s.soft ? ', мягкая' : '') + ') → '
              + (s.pred_task ? 'эт. ' + (s.pred_task.etazh || '—') + ': ' + dd(s.pred_task.nachalo) + ' – ' + dd(s.pred_task.okonchanie)
                : 'предшественника на этаже нет')}</span>
        </label>))}
      <div style={{ fontWeight: 600, margin: '8px 0 4px' }}>Только у этой задачи</div>
      {!d.ruchnye.length && <div className="pw-muted">нет</div>}
      {d.ruchnye.map(m => (
        <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
          <span style={m.iskl ? { color: '#b42318' } : undefined}>
            {(m.nomer != null ? '№' + m.nomer + ' · ' : '') + m.rabota + ' · ' + m.korpus
              + (m.etazh ? ' · эт. ' + m.etazh : ' · корпус') + ' (' + m.tip + (m.zapas ? ', задержка ' + m.zapas : '')
              + (m.soft ? ', мягкая' : '') + '): ' + dd(m.nachalo) + ' – ' + dd(m.okonchanie) + (m.iskl ? ' — исключена!' : '')}</span>
          {can && <a title="удалить ручную связь" style={{ cursor: 'pointer', color: '#b42318', fontWeight: 700 }}
            onClick={() => void delRuch(m.id)}>×</a>}
        </div>))}
      {can && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', margin: '10px 0 6px', alignItems: 'center' }}>
          <span>Добавить ручную</span><span className="pw-muted">корпус · работа · этаж · тип · запас</span>
          <span></span>
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={f.korpus} onChange={e => setF(s => ({ ...s, korpus: e.target.value, etazh: '' }))}>
              <option value=""></option>{korpusa.map(k => <option key={k} value={k}>{k}</option>)}</select>
            <PoleSpiska value={f.rabota} opts={works} placeholder="работа" style={{ width: 170 }}
              onChange={v => setF(s => ({ ...s, rabota: v }))} onEnter={() => void add()} />
            <select value={f.etazh} onChange={e => setF(s => ({ ...s, etazh: e.target.value }))}>
              <option value="">— корпус целиком —</option>
              {(struct[f.korpus] || []).map(fl => <option key={fl.id} value={String(fl.etazh)}>{fl.etazh}</option>)}</select>
            <select value={f.tip} onChange={e => setF(s => ({ ...s, tip: e.target.value }))}>
              <option value="ОН">оконч. → нач.</option><option value="НН">нач. → нач.</option></select>
            <input placeholder="задержка" style={{ width: 70 }} value={f.zapas} onChange={e => setF(s => ({ ...s, zapas: e.target.value }))} />
            <label className="pw-muted" title="можно идти вперёд, если предшественник отстаёт">
              <input type="checkbox" style={{ width: 'auto' }} checked={f.soft}
                onChange={e => setF(s => ({ ...s, soft: e.target.checked }))} /> мягкая</label>
            <button onClick={() => void add()}>Добавить</button>
          </span>
        </div>
        <div className="pw-muted">после правок — «Актуализировать»</div>
      </>}
    </Pop>
  );
}

function BulkLinksPop({ at: a, pid, ids, korpusa, works, struct, onClose, onDone, onFail }: {
  at: Anchor; pid: number; ids: number[]; korpusa: string[]; works: string[]; struct: Record<string, Floor[]>;
  onClose: () => void; onDone: (m: string) => void; onFail: (m: string) => void }) {
  const [v, setV] = useState({ rabota: '', rezhim: 'etazh', sdvig: '', korpus: korpusa[0] || '', etazh: '',
    tip: 'ОН', zapas: '', soft: false });
  async function go() {
    if (!v.rabota.trim()) { onFail('Укажите работу-предшественника'); return; }
    const body: Record<string, unknown> = { ids, rabota: v.rabota.trim(), rezhim: v.rezhim,
      sdvig: v.sdvig.trim() || 0, tip: v.tip, zapas: v.zapas.trim() || null, soft: v.soft };
    if (v.rezhim === 'zadacha') {
      const r0 = await call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid })
        + '?korpus=' + encodeURIComponent(v.korpus) + '&rabota=' + encodeURIComponent(v.rabota.trim()) + '&limit=20000');
      const et = v.etazh || null;
      const cand = r0.ok ? r0.data.rows.find(x => (x.etazh || null) === et) : null;
      if (!cand) { onFail('Задача-предшественник не найдена'); return; }
      body.pred_task_id = cand.id;
    }
    const r = await call<{ sozdano: number; propuscheno: number }>('post',
      p('/api/projects/{pid}/task-links/bulk', { pid }), body);
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Ручных связей создано: ' + r.data.sozdano + ', пропущено: ' + r.data.propuscheno
      + '. Нажмите «Актуализировать», чтобы обновить даты.');
  }
  return (
    <Pop at={a} title={'Предшественник для ' + ids.length + ' задач'} onClose={onClose} width={460}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Каждой выбранной задаче добавится ручная связь с указанной
        работой: на том же этаже, на этаже со сдвигом или с одной конкретной задачей.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', alignItems: 'center' }}>
        <span>Работа-предшественник</span>
        <PoleSpiska value={v.rabota} opts={works} placeholder="работа"
          onChange={x => setV(s => ({ ...s, rabota: x }))} />
        <span>Режим</span>
        <select value={v.rezhim} onChange={e => setV(s => ({ ...s, rezhim: e.target.value }))}>
          <option value="etazh">тот же этаж</option><option value="sdvig">этаж со сдвигом</option>
          <option value="zadacha">конкретная задача</option></select>
        {v.rezhim === 'sdvig' && <><span>Сдвиг этажей</span>
          <input placeholder="напр. 2 или -1" style={{ width: 100 }} value={v.sdvig}
            onChange={e => setV(s => ({ ...s, sdvig: e.target.value }))} /></>}
        {v.rezhim === 'zadacha' && <><span>Задача</span>
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select value={v.korpus} onChange={e => setV(s => ({ ...s, korpus: e.target.value, etazh: '' }))}>
              {korpusa.map(k => <option key={k} value={k}>{k}</option>)}</select>
            <select value={v.etazh} onChange={e => setV(s => ({ ...s, etazh: e.target.value }))}>
              <option value="">— корпус целиком —</option>
              {(struct[v.korpus] || []).map(fl => <option key={fl.id} value={String(fl.etazh)}>{fl.etazh}</option>)}</select>
          </span></>}
        <span>Тип</span>
        <select value={v.tip} onChange={e => setV(s => ({ ...s, tip: e.target.value }))}>
          <option value="ОН">окончание → начало (ОН)</option><option value="НН">начало → начало (НН)</option></select>
        <span>Задержка, дн</span>
        <input style={{ width: 70 }} value={v.zapas} onChange={e => setV(s => ({ ...s, zapas: e.target.value }))} />
        <span>Мягкая</span>
        <label className="pw-muted"><input type="checkbox" style={{ width: 'auto' }} checked={v.soft}
          onChange={e => setV(s => ({ ...s, soft: e.target.checked }))} /> можно идти вперёд, если предшественник отстаёт</label>
      </div>
      <div className="bar" style={{ margin: '10px 0 0' }}><button onClick={() => void go()}>Назначить</button></div>
    </Pop>
  );
}

/** id шаблона «Базовый» проекта — для связи, заданной именем работы */
async function baseTpl(pid: number): Promise<number | null> {
  const r = await call<{ id: number; name: string }[]>('get', '/api/link-templates?project=' + pid);
  if (!r.ok) return null;
  const t = r.data.find(x => x.name.toLowerCase() === 'базовый');
  if (t) return t.id;
  const c = await call<{ id: number }>('post', '/api/link-templates', { name: 'Базовый', project: pid });
  return c.ok ? c.data.id : null;
}

/** Строка «＋» внизу «Ручного планирования»: работа, длительность, предшественник.
    Enter сохраняет; в поле работы можно вставить список из буфера — строками
    «работа [tab] дней [tab] предшественник» (2026-09-08). */
function AddTaskRow({ ctx, meta, struct, korpusa, onAdded, onFail }: {
  ctx: ShellCtx; meta: Record<string, Work>; struct: Record<string, Floor[]>; korpusa: string[];
  onAdded: (m: string) => void; onFail: (m: string) => void }) {
  const [v, setV] = useState({ work: '', dlit: '', pred: '', korpus: korpusa[0] || '', etazh: '' });
  const manualWorks = Object.values(meta).filter(w => w.raskatka === 'вручную'
    || kontury(w.kontur).includes('Ручной')).map(w => w.name).sort();
  const allWorks = Object.keys(meta).sort();

  /** создать одну задачу; возвращает № созданной задачи (для цепочки) */
  async function sozdat(name: string, dlitRaw: string, pred: string, korp: string, et: string) {
    if (!name || !korp) { onFail('Укажите работу и выберите корпус'); return null; }
    if (dlitRaw !== '' && isNaN(+dlitRaw.replace(',', '.'))) { onFail('Длительность — число дней'); return null; }
    if (pred && pred === name) { onFail('Работа не может быть предшественником самой себе'); return null; }
    let wid = meta[name]?.id;
    let created = '';
    if (!wid) {
      const rc = await call<{ id: number }>('post', p('/api/projects/{pid}/works', { pid: ctx.pid }),
        { name, kontur: 'Ручной', raskatka: 'вручную' });
      if (!rc.ok) { onFail(rc.error.message); return null; }
      wid = rc.data.id;
      created = ' (работа создана в справочнике с контуром «Ручной»)';
    }
    const r = await call<{ id: number; nomer?: number | null }>('post',
      p('/api/projects/{pid}/tasks/single', { pid: ctx.pid }),
      { work_id: wid, korpus: korp, etazh: et || null, dlit_ruchnaya: dlitRaw === '' ? null : dlitRaw });
    if (!r.ok) { onFail(r.error.message); return null; }
    let linkNote = '';
    if (pred && /^\s*\d+/.test(pred)) {
      const rl = await call('patch', p('/api/tasks/{tid}', { tid: r.data.id }), { rev: 1, pred_ruch: pred });
      if (rl.ok) linkNote = ' ← №' + pred; else onFail('Предшественник не назначен: ' + rl.error.message);
    } else if (pred) {
      const tid = await baseTpl(ctx.pid);
      if (tid) {
        const rl = await call('post', p('/api/link-templates/{tid}/rows', { tid }), { work: name, pred });
        if (rl.ok || rl.error.code === 'exists' || /уже есть/i.test(rl.error.message)) linkNote = ' ← ' + pred;
        else onFail('Связь не создана: ' + rl.error.message);
      }
    }
    const nomer = r.data.nomer != null ? String(r.data.nomer) : name;
    onAdded('Задача добавлена: ' + name + ' · ' + korp + (et ? ' · эт. ' + et : '')
      + (dlitRaw ? ' · ' + dlitRaw + ' дн' : '') + linkNote + created + '. Даты — после «Актуализировать».');
    return nomer;
  }

  async function save() {
    const nomer = await sozdat(v.work.trim(), v.dlit.trim(), v.pred.trim(), v.korpus, v.etazh);
    if (nomer === null) return;
    setV(s => ({ ...s, work: '', dlit: '', pred: '', etazh: '' }));
  }

  /** вставка списка из буфера: строки «работа [tab] дней [tab] предшественник» */
  async function vstavit(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/\r?\n/).map(x => x.trim()).filter(Boolean);
    if (lines.length < 2 && !text.includes('\t')) return;      // обычная вставка слова
    e.preventDefault();
    let pred = v.pred.trim();
    let n = 0;
    for (const line of lines) {
      const [name, dlit, pr] = line.split('\t');
      const nomer = await sozdat((name || '').trim(), (dlit || '').trim(),
        (pr || '').trim() || pred, v.korpus, v.etazh);
      if (nomer === null) break;
      n++;
      pred = '';
    }
    setV(s => ({ ...s, work: '', dlit: '', pred: '' }));
    if (n > 1) onAdded('Добавлено задач: ' + n + '. Даты — после «Актуализировать».');
  }

  return (
    <tr id="raRow">
      <td className="fx-n ra-plus">＋</td>
      <td className="fx-k">
        <select value={v.korpus} title="Корпус задачи" style={{ width: '100%' }}
          onChange={e => setV(s => ({ ...s, korpus: e.target.value, etazh: '' }))}>
          <option value=""></option>{korpusa.map(k => <option key={k} value={k}>{k}</option>)}</select>
      </td>
      <td className="fx-e">
        <select value={v.etazh} title="Этаж; пусто — задача на весь корпус" style={{ width: '100%' }}
          onChange={e => {
            setV(s => ({ ...s, etazh: e.target.value }));
            if (e.target.value !== '') setTimeout(() => void save(), 0);
          }}>
          <option value="">весь корпус</option>
          {(struct[v.korpus] || []).map(fl => <option key={fl.id} value={String(fl.etazh)}>{fl.etazh}</option>)}</select>
      </td>
      <td className="col-zahv"></td>
      <td className="fx-w">
        <PoleSpiska value={v.work} opts={manualWorks} placeholder="работа: выберите или введите новую"
          style={{ width: '100%' }} onChange={x => setV(s => ({ ...s, work: x }))}
          onEnter={() => void save()} onPaste={e => void vstavit(e)}
          title="Существующая ручная работа — из списка; новое название создаст работу в справочнике с контуром «Ручной». Enter — сохранить; можно вставить список строками из буфера" />
      </td>
      <td className="col-itog col-b"></td>
      <td className="col-itog"></td>
      <td className="col-itog col-e">
        <input placeholder="дн" className="num" style={{ width: '100%' }} title="Длительность, дней" value={v.dlit}
          onChange={e => setV(s => ({ ...s, dlit: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void save(); } }} />
      </td>
      <td colSpan={99} className="ra-cell">
        <PoleSpiska value={v.pred} opts={allWorks} placeholder="предшественник (№ задачи или работа)"
          style={{ width: 210 }} onChange={x => setV(s => ({ ...s, pred: x }))}
          onEnter={() => void save()}
          title="Предшественник (окончание → начало): номер задачи ставит ручную связь, название работы — связь в схеме «Базовый»" />
        <span className="pw-muted">Enter — строка сохраняется, курсор остаётся для следующей</span>
      </td>
    </tr>
  );
}


/** Замечание к задаче: `gruppa` — «ошибка» или «конфликт», `chto` — названия
 *  сработавших проверок (показываем в подсказке строки). */
type Problema = { tid: number; gruppa: string; chto: string[] };
export type SvodkaOshibok = { vsego: number; vidy: { chto: string; gruppa: string; n: number }[] };

/** Больше стрелок экран не читает, а рисование заметно тормозит (2026-09-10). */
const PREDEL_STRELOK = 400;

/** Пара для стрелки: `ot` — задача-предшественник, `do` — задача, `vid` —
 *  svyaz (связь справочника) | ruch (ручной предшественник) | resurs (бригада). */
type Para = { ot: number; do: number; vid: string };

/** Слой стрелок над Гантом (2026-09-10).
 *
 *  Координаты не вычисляем по датам, а берём у самих полос в разметке: так
 *  стрелка не разъезжается с полосой ни при перетаскивании, ни при прокрутке.
 *  Рисуем только те пары, у которых обе задачи сейчас на экране, — таблица
 *  виртуализована, за окном строк просто нет. `risuj` — любое меняющееся
 *  число (прокрутка, число строк): по нему пересчитываем.
 */
function SvyaziSloj({ box, pary, rabotaZadachi, risuj }: {
  box: React.RefObject<HTMLDivElement>; pary: Para[];
  rabotaZadachi: Map<number, string>; risuj: number }) {
  const [linii, setLinii] = useState<{ d: string; vid: string; key: string }[]>([]);
  const [razmer, setRazmer] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !pary.length) { setLinii([]); return; }
    const kontejner = el.getBoundingClientRect();
    type Tochka = { x1: number; x2: number; y: number; h: number };
    const mesto = (bar: Element): Tochka => {
      const r = bar.getBoundingClientRect();
      return {
        x1: r.left - kontejner.left + el.scrollLeft,
        x2: r.right - kontejner.left + el.scrollLeft,
        y: r.top - kontejner.top + el.scrollTop + r.height / 2,
        h: r.height,
      };
    };
    const koord: Record<number, Tochka> = {};
    const koordGrp: Record<string, Tochka> = {};
    el.querySelectorAll('tr[data-tid]').forEach(tr => {
      const bar = tr.querySelector('.viz-bar');
      if (bar) koord[Number((tr as HTMLElement).dataset.tid)] = mesto(bar);
    });
    // свёрнутая работа: вместо задач видна одна суммарная полоса — ведём к ней
    el.querySelectorAll('tr[data-grp]').forEach(tr => {
      const bar = tr.querySelector('.viz-bar');
      if (bar) koordGrp[String((tr as HTMLElement).dataset.grp)] = mesto(bar);
    });
    const tochka = (tid: number): Tochka | null => {
      if (koord[tid]) return koord[tid];
      const rab = rabotaZadachi.get(tid);
      return (rab && koordGrp[rab]) || null;
    };
    const out: { d: string; vid: string; key: string }[] = [];
    const bylo = new Set<string>();
    for (const pr of pary) {
      if (out.length >= PREDEL_STRELOK) break;
      const a = tochka(pr.ot), b = tochka(pr.do);
      if (!a || !b || a === b) continue;             // не видно или это одна и та же полоса
      // после подъёма к группам одна и та же стрелка может получиться много раз
      const klyuch = Math.round(a.x2) + ':' + Math.round(a.y) + '→'
        + Math.round(b.x1) + ':' + Math.round(b.y) + ':' + pr.vid;
      if (bylo.has(klyuch)) continue;
      bylo.add(klyuch);
      // ломаная «от конца предшественника к началу последователя», как в Ганте:
      // немного вправо, вертикаль, подход к началу полосы стрелкой
      const x0 = a.x2, y0 = a.y, x1 = b.x1, y1 = b.y;
      const otstup = 8;
      // если последователь начинается не правее конца предшественника, стрелка
      // делает горизонтальный шаг назад — сразу под (над) полосой предшественника,
      // в зазоре между строками, а не посередине пути: середина попадала на чужую
      // полосу (суммарную), и казалось, что связь ведёт к ней (замечание 2026-09-13)
      const shag = y1 > y0 ? y0 + a.h / 2 + 4 : y0 - a.h / 2 - 4;
      const d = x1 >= x0 + otstup * 2
        ? `M ${x0} ${y0} H ${x1 - otstup} V ${y1} H ${x1}`
        : `M ${x0} ${y0} H ${x0 + otstup} V ${shag} H ${x1 - otstup} V ${y1} H ${x1}`;
      out.push({ d, vid: pr.vid, key: klyuch });
    }
    setLinii(out);
    setRazmer({ w: el.scrollWidth, h: el.scrollHeight });
  }, [box, pary, rabotaZadachi, risuj]);

  if (!linii.length) return null;
  return (
    <svg className="viz-svyazi" width={razmer.w} height={razmer.h}>
      <defs>
        <marker id="strelka" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6"
          orient="auto-start-reverse">
          <path d="M 0 1 L 7 4 L 0 7 z" />
        </marker>
      </defs>
      {linii.map(l => (
        <path key={l.key} d={l.d} className={'viz-svyaz viz-svyaz-' + l.vid}
          markerEnd="url(#strelka)" />
      ))}
    </svg>
  );
}
